import usePageFilterOptions from "../hooks/usePageFilterOptions";
import ContactFiltersDrawer from "../components/ContactFiltersDrawer";
import { buildContactListQuery, contactFilterPills } from "../utils/contactListQuery";
import { notifyCompanyRelationshipsChanged } from "../utils/companyRelationshipCounts";
import CompanyTableCell, { PipelineCompanyTableCell } from "../components/CompanyTableCell";
// src/pages/JobBoardTable.jsx
import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { openInterviewScheduler } from "../utils/interviewScheduling";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import { pipelineStageKey, stageColor, recruitingMetricColors } from "../utils/pipelineStages";
import { JOB_EXPORT_TAB_FIELDS } from "../utils/exportTabFields";
import { useLocation, useNavigate, useNavigationType } from "react-router-dom";
import useAuthController from "../hooks/useAuthController";
import {
  Box,
  Card,
  Chip,
  Typography,
  IconButton,
  TableCell,
  Avatar,
  Divider,
  TextField,
  Button,
  Stack,
  Menu,
  MenuItem,
  CircularProgress,
  Alert,
} from "@mui/material";
import { BoltOutlined as BoltOutlinedIcon } from "../components/icons/FigmaControlIcons";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";

import Breadcrumb from "../components/Breadcrumb";
import GenericDataTable from "../components/GenericDataTable";
import RecruitingPipelineTable from "../components/RecruitingPipelineTable";
import InfoActionIcons from "../components/InfoActionIcons";
import StatsSlider from "../components/StatsSlider";
import TopListHeaderBar from "../components/TopListHeaderBar";
import TabNavigationBar from "../components/TabNavigationBar";
import JobDetailsView from "../components/JobDetailsView";
import BulkActionsBar from "../components/BulkActionsBar";
import ReusableRightDrawer from "../components/ReusableRightDrawer";
import ListTableTabsTemplate from "../components/layouts/ListTableTabsTemplate";
import AddJobDialog from "../components/PopupWindows/AddJobDialog.jsx";
import AddToSequenceDialog from "../components/PopupWindows/AddToSequenceDialog.jsx";
import CsvImportDialog from "../components/CsvImportDialog";
import useCsvImport from "../hooks/useCsvImport";
import JobBulkAssignTeamMemberAction from "../components/JobBulkAssignTeamMemberAction.jsx";
import { buildJobSequenceItem } from "../utils/sequenceItems";
import { formatLocalDate, toTimeValue } from "../utils/dateTime";
import { formatRelativeTimeShort } from "../utils/relativeTime";
import {
  createSidebarResetState,
  getSidebarResetToken,
} from "../utils/sidebarNavigation";
import {
  iconPillButtonSx,
  pillButtonInteractiveSx,
  toolbarPillButtonSx,
  withPillButtonClassName,
} from "../utils/pillButtonStyles";
import {
  dedupeHiringManagerRows,
  fetchHiringManagers,
  isHiringManagerContactRow,
  normalizeHiringManagerEntityType,
} from "../utils/hiringManagers";
import { normalizeContactRoleLabel } from "../utils/contactRoles";
import {
  areSamePersonNames,
  getDistinctJobHiringManagerName,
  getRecruiterOwnerName,
  isRecruiterContact,
  sanitizeJobCompanyContactFields,
} from "../utils/recruiterOwnership";
import {
  groupLocationOption,
  normalizeLocationFilterValues,
} from "../utils/locationFilters";
import { buildDetailFieldOptions } from "../utils/detailFieldOptions";
import { normalizeIndustrySelectionValues } from "../utils/industrySelections";
import { isActiveClientCompanyStatus } from "../utils/companyStatuses";
import PopupMultiSelectField, { popupFilterInputSx } from "../components/PopupMultiSelectField";
import LocationMultiSelectField from "../components/LocationMultiSelectField";
import CompanyAdvancedSearchView from "../components/CompanyAdvancedSearchView";
import { buildOpenCompanyNavPayload } from "../utils/companyNavigation";
import { InlineBackgroundTaskLabel } from "../components/EntityBackgroundTasks";
import { syncJobBackgroundWork } from "../utils/entityBackgroundWorkState";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { COMPANY_PROFILE_TABS, DETAIL_VIEW_TABS, findTabLabel } from "../utils/breadcrumbs";
import {
  buildPublicSharePayload,
  createAndCopyPublicShareLink,
  SHARE_LINK_COPIED_MESSAGE,
} from "../utils/publicShareLinks";
import { JOB_STATUS_VALUES } from "../utils/jobStatusOptions";

// Replaced Material Icons with Iconsax (no other changes)
import {
  Filter,
  Add,
  Briefcase,
  Magicpen,
  Eye,
  CloseCircle,
  Call,
  Sms,
  Calendar1,
  People,
  Clock,
  Trash,
} from "iconsax-react";

/* ---------------- Status Styles ---------------- */
const JOB_STATUS_OPTIONS = JOB_STATUS_VALUES;
const NAV_CACHE_KEY = "rpo:openCompanyNav";

const JOB_STATUS_DISPLAY_BY_KEY = {
  draft: "Draft",
  pending: "Draft",
  "for review": "Draft",
  scheduled: "Draft",
  open: "Open",
  active: "Open",
  "accepting candidates": "Open",
  approved: "Open",
  "in progress": "Open",
  "on hold": "On Hold",
  "onhold": "On Hold",
  filled: "Filled",
  "closed ( filled )": "Filled",
  placed: "Filled",
  completed: "Filled",
  closed: "Closed",
  "closed ( canceled )": "Closed",
  "closed ( cancelled )": "Closed",
  rejected: "Closed",
  archived: "Closed",
};

const normalizeJobStatusDisplay = (value) => {
  const raw = cleanText(value).toLowerCase();
  if (!raw) return "";
  return JOB_STATUS_DISPLAY_BY_KEY[raw] || cleanText(value);
};

const normalizeJobStatusRequestValue = (value) => {
  const normalizedDisplay = normalizeJobStatusDisplay(value);
  if (normalizedDisplay === "Filled") return "Closed ( Filled )";
  if (normalizedDisplay === "Closed") return "Closed ( Canceled )";
  return normalizedDisplay;
};

const STATUS_STYLE = {
  Draft: { bg: "#F5F3FF", color: "#7C3AED" },
  Open: { bg: "#dcfce7", color: "#16a34a" },
  "On Hold": { bg: "#FEF3C7", color: "#D97706" },
  "Filled": { bg: "#EEF2FF", color: "#3451F4" },
  "Closed": { bg: "#FEE2E2", color: "#DC2626" },
  // Legacy aliases kept for existing rows that still use older labels.
  New: { bg: "#EEF4FF", color: "#3451F4" },
  Active: { bg: "#dcfce7", color: "#16a34a" },
  "Accepting Candidates": { bg: "#dcfce7", color: "#16a34a" },
  Placed: { bg: "#EEF2FF", color: "#3451F4" },
  "On hold": { bg: "#FEF3C7", color: "#D97706" },
  "For Review": { bg: "#F5F3FF", color: "#7C3AED" },
  Pending: { bg: "#F5F3FF", color: "#7C3AED" },
  Approved: { bg: "#dcfce7", color: "#16a34a" },
  Rejected: { bg: "#FEE2E2", color: "#DC2626" },
  Completed: { bg: "#EEF2FF", color: "#3451F4" },
  Scheduled: { bg: "#F5F3FF", color: "#7C3AED" },
  Archived: { bg: "#FEE2E2", color: "#DC2626" },
  "In Progress": { bg: "#dcfce7", color: "#16a34a" },
};
const JOB_PRIORITY_STYLE = {
  Low: { bg: "#F2F4F7", color: "#475467" },
  Medium: { bg: "#EEF4FF", color: "#3451F4" },
  High: { bg: "#FEF3C7", color: "#B54708" },
  Urgent: { bg: "#FEE2E2", color: "#B42318" },
};
const WORK_ARRANGEMENT_STYLE = {
  Remote: { bg: "#EEF4FF", color: "#3451F4" },
  Hybrid: { bg: "#F5F3FF", color: "#7C3AED" },
  "On-site": { bg: "#ECFDF3", color: "#027A48" },
};
const CLIENT_PORTAL_STYLE = {
  Visible: { bg: "#ECFDF3", color: "#027A48" },
  Hidden: { bg: "#F2F4F7", color: "#667085" },
};
const JOBS_BULK_ACTIONS_LABEL_INSET_PX = 22;
const JOBS_SELECTION_COLUMN_WIDTH_PX = 44;
const JOBS_SELECTION_CELL_PADDING_Y_PX = 10;
const JOB_EXPORT_MENU_OPTIONS = [{ label: "CSV Export", value: "csv_export" }];
const JOB_ARCHIVE_MENU_OPTIONS = [{ label: "Soft Archive", value: "soft_archive" }];
const JOB_STATS_ITEM_DESCRIPTIONS = {
  "status:open": "Jobs currently open and actively being worked.",
  assignedRecruiters: "Open jobs with a recruiter assigned.",
  applied: "Candidates who applied through public job postings.",
  matched: "Candidates matched by AI and awaiting recruiter review.",
  sourced: "Candidates manually sourced and attached to jobs.",
  contacted: "Candidates contacted regarding positions.",
  screened: "Candidates who completed recruiter screening.",
  presented: "Candidates submitted to hiring managers.",
  interviewing: "Candidates actively interviewing.",
  assessments: "Candidates completing assessments.",
  offers: "Candidates with active offers.",
  hires: "Successful hires.",
};
const JOB_PIPELINE_STAGE_FILTER_VALUES_BY_STAT_ID = {
  applied: ["applied"],
  matched: ["matched"],
  sourced: ["sourced"],
  contacted: ["contacted"],
  screened: ["screened"],
  presented: ["presented"],
  interviewing: ["interview", "interviewing"],
  assessments: ["assessment"],
  offers: ["offer"],
  hires: ["hired"],
};
const ADMIN_ROLES = new Set(["admin", "administrator", "system_user"]);
const TABLE_META_TEXT_SX = {
  fontSize: 12,
  color: "#98A2B3",
  fontFamily: "Be Vietnam Pro",
  lineHeight: 1.35,
};
const ACTIVE_CLIENT_BADGE_SX = {
  height: 20,
  borderRadius: "999px",
  backgroundColor: "#ECFDF3",
  color: "#027A48",
  fontSize: 10.5,
  fontWeight: 700,
  fontFamily: "Be Vietnam Pro",
  "& .MuiChip-label": {
    px: 1,
  },
};
const DATE_LABEL_FORMATTER = new Intl.DateTimeFormat(undefined, {
  month: "short",
  day: "numeric",
  year: "numeric",
});

const API_BASE = process.env.REACT_APP_API_BASE;
const JOBS_UPDATED_EVENT = "rpo:jobs-updated";
const normalizeJobRouteTab = (value) => {
  const normalized = String(value || "").toLowerCase();
  const validTabs = ["details", "stages", "matches", "activities"];
  return validTabs.includes(normalized) ? normalized : "";
};

const normalizeCompanyOriginTab = (value) => {
  const normalized = String(value || "").toLowerCase();
  const validTabs = ["details", "leads", "contacts", "jobs", "candidates", "activities"];
  return validTabs.includes(normalized) ? normalized : "jobs";
};

/* ---------------- Helpers ---------------- */
const toQuery = (obj) => {
  const params = new URLSearchParams();
  Object.entries(obj || {}).forEach(([k, v]) => {
    if (v == null) return;
    if (Array.isArray(v)) {
      v.filter(Boolean).forEach((item) => params.append(k, item));
    } else if (String(v).trim() !== "") {
      params.set(k, v);
    }
  });
  const s = params.toString();
  return s ? `?${s}` : "";
};

const uniq = (arr) =>
  Array.from(new Set([].concat(arr || []).filter(Boolean).map((x) => String(x).trim()).filter(Boolean)));

const cleanText = (value) => String(value ?? "").trim();
const normalizeStatValue = (value) => cleanText(value).toLowerCase();
const normalizeRecruitingPipelineStage = (value) => {
  const stage = cleanText(value).toLowerCase().replace(/[\s_-]+/g, " ");
  if (["interview", "interviewing"].includes(stage)) return "interview";
  if (["offer", "offered", "offers"].includes(stage)) return "offer";
  if (["hired", "hire", "placed"].includes(stage)) return "hired";
  if (["assessment", "assessments"].includes(stage)) return "assessment";
  return stage;
};
const normalizeFilterChipValue = (value) => cleanText(value).toLowerCase();
const removeFilterValueFromList = (values = [], target) =>
  uniq(
    (values || []).filter(
      (value) => normalizeFilterChipValue(value) !== normalizeFilterChipValue(target)
    )
  );

const toCsvValue = (value) => `"${String(value ?? "").replace(/\r?\n/g, " ").replace(/"/g, '""')}"`;

const getAvatarLetters = (value, fallback = "NA") => {
  const parts = String(value || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (!parts.length) return fallback;

  return parts
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || "")
    .join("");
};

const parsePeopleSource = (value) => {
  if (Array.isArray(value)) return value;
  if (value && typeof value === "object") return [value];

  const text = cleanText(value);
  if (!text) return [];

  if (text.startsWith("[") && text.endsWith("]")) {
    try {
      const parsed = JSON.parse(text);
      if (Array.isArray(parsed)) return parsed;
    } catch {
      // Fall through to plain-text parsing.
    }
  }

  return text
    .split(/\s*(?:,|;|\|)\s*/g)
    .map((item) => item.trim())
    .filter(Boolean);
};

const normalizePeople = (...sources) => {
  const seen = new Set();

  return sources
    .flatMap(parsePeopleSource)
    .map((item) => {
      if (typeof item === "string") {
        const name = cleanText(item);
        return name ? { name, avatar: "" } : null;
      }

      if (!item || typeof item !== "object") return null;

      const name = cleanText(
        item.name ||
          item.fullName ||
          item.full_name ||
          item.label ||
          item.title ||
          item.owner ||
          item.owner_name ||
          item.recruiter_name ||
          item.contact_name ||
          item.team_member
      );
      const avatar = cleanText(
        item.avatar ||
          item.avatarUrl ||
          item.avatar_url ||
          item.profile_image ||
          item.profileImage ||
          item.photo ||
          item.photoUrl ||
          item.photo_url ||
          item.image ||
          item.imageUrl ||
          item.image_url ||
          item.user_avatar_url ||
          item.recruiter_profile_image ||
          item.src
      );

      if (!name && !avatar) return null;
      return { name: name || "Unknown", avatar };
    })
    .filter(Boolean)
    .filter((item) => {
      const key = cleanText(item.name || item.avatar).toLowerCase();
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
};

const toNonNegativeNumber = (value, fallback = 0) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) && numericValue >= 0 ? numericValue : fallback;
};

const normalizeJobPriorityLabel = (value) => {
  const normalized = cleanText(value).toLowerCase();
  if (!normalized) return "";
  if (normalized === "low") return "Low";
  if (normalized === "medium" || normalized === "med") return "Medium";
  if (normalized === "high") return "High";
  if (normalized === "urgent" || normalized === "critical") return "Urgent";
  return cleanText(value);
};

const normalizeWorkArrangementLabel = (value) => {
  const normalized = cleanText(value).toLowerCase();
  if (!normalized) return "";
  if (normalized === "remote") return "Remote";
  if (normalized === "hybrid" || normalized === "hibryd") return "Hybrid";
  if (normalized === "on-site" || normalized === "onsite" || normalized === "on site") {
    return "On-site";
  }
  return "";
};

const normalizeClientPortalVisibility = (row = {}) => {
  const rawValue =
    row?.client_portal_visibility ??
    row?.client_portal ??
    row?.portal_visibility ??
    row?.portal_status ??
    row?.visible_in_client_portal ??
    row?.is_client_portal_visible ??
    row?.client_visible ??
    row?.visible_to_client ??
    "";

  if (typeof rawValue === "boolean") return rawValue ? "Visible" : "Hidden";

  const normalized = cleanText(rawValue).toLowerCase();
  if (!normalized) return "";
  if (["visible", "show", "shown", "public", "published", "true", "1", "yes"].includes(normalized)) {
    return "Visible";
  }
  if (["hidden", "hide", "internal", "private", "false", "0", "no"].includes(normalized)) {
    return "Hidden";
  }
  return "";
};

const getJobLastActivityValue = (row = {}) =>
  row?.job_list_last_activity_time ||
  row?.last_activity_time ||
  row?.last_activity_date ||
  row?.last_activity_at ||
  row?.updated_at ||
  row?.created_at ||
  "";

const withJobActivitySortFields = (row = {}) => {
  const lastActivityValue = getJobLastActivityValue(row);

  return {
    ...row,
    job_list_last_activity_time:
      row?.job_list_last_activity_time || lastActivityValue || null,
    last_activity_time:
      row?.last_activity_time || row?.job_list_last_activity_time || lastActivityValue || null,
  };
};

const formatJobRelativeDate = (value, { emptyValue = "-" } = {}) => {
  return formatRelativeTimeShort(value, { emptyValue });
};

const formatJobOpenedDate = (value) => {
  const timestamp = toTimeValue(value);
  return Number.isFinite(timestamp) ? DATE_LABEL_FORMATTER.format(new Date(timestamp)) : "-";
};

const getJobHiringManagerDisplay = (row = {}) => {
  const normalizedRow = sanitizeJobCompanyContactFields(row);
  const recruiterName = getRecruiterOwnerName(normalizedRow);
  const fallbackName = cleanText(
    normalizedRow.contact_name || normalizedRow.hiring_manager_name || getDistinctJobHiringManagerName(normalizedRow)
  );
  const visibleContact = Array.isArray(normalizedRow.contacts)
    ? normalizedRow.contacts.find((item) => {
        const role = cleanText(item?.role || item?.title || item?.contact_kind).toLowerCase();
        const name = cleanText(item?.name || item?.contact_name || item?.hiring_manager_name);
        if (!name) return false;
        if (/owner|recruiter/i.test(role)) return false;
        return !areSamePersonNames(name, recruiterName);
      })
    : null;

  return {
    name: cleanText(
      visibleContact?.name ||
        visibleContact?.contact_name ||
        visibleContact?.hiring_manager_name ||
        fallbackName
    ),
    title: cleanText(
      visibleContact?.title ||
        visibleContact?.role ||
        normalizedRow.contact_title ||
        normalizedRow.contact_role ||
        normalizedRow.hiring_manager_title ||
        normalizedRow.hiring_manager_role
    ),
  };
};

// The owner is the recruiter who owns/created the saved job record. It is
// distinct from the external hiring-manager contact and must never be inferred
// from the current logged-in user when the Jobs list is rendered.
const getSavedJobOwnerDisplay = (row = {}) =>
  cleanText(
    row?.owner ||
      row?.owner_name ||
      row?.recruiter_name ||
      row?.created_by_name ||
      row?.assigned_team_member
  );

const buildJobAssignedTeamPeople = (row = {}) => {
  const recruiterContacts = Array.isArray(row?.contacts)
    ? row.contacts.filter((item) => {
        const role = cleanText(item?.role || item?.title || item?.contact_kind).toLowerCase();
        return /owner|recruiter|sourcer|account/.test(role);
      })
    : [];

  const assignedEntries = [];
  const recruiterName = getRecruiterOwnerName(row);
  if (recruiterName) {
    assignedEntries.push({
      name: recruiterName,
      avatar:
        row.owner_avatar ||
        row.owner_avatar_url ||
        row.owner_profile_image ||
        row.recruiter_profile_image ||
        row.profile_image ||
        "",
    });
  }

  const assignedTeamMember = cleanText(row?.assigned_team_member);
  if (assignedTeamMember) {
    assignedEntries.push({
      name: assignedTeamMember,
      avatar:
        row.assigned_team_member_avatar ||
        row.assigned_team_avatar ||
        row.recruiter_profile_image ||
        row.profile_image ||
        "",
    });
  }

  const sourcerName = cleanText(row?.sourcer_name || row?.sourcer);
  if (sourcerName) {
    assignedEntries.push({
      name: sourcerName,
      avatar: row?.sourcer_avatar || row?.sourcer_avatar_url || "",
    });
  }

  const accountManagerName = cleanText(
    row?.account_manager_name || row?.account_manager || row?.accountManagerName
  );
  if (accountManagerName) {
    assignedEntries.push({
      name: accountManagerName,
      avatar: row?.account_manager_avatar || row?.account_manager_avatar_url || "",
    });
  }

  return normalizePeople(recruiterContacts, assignedEntries);
};

const getJobPipelineSummary = (row = {}) => ({
  total: toNonNegativeNumber(
    row?.pipeline_total_count ?? row?.candidates_count ?? row?.num_applications ?? row?.application_count,
    0
  ),
  applied: toNonNegativeNumber(row?.pipeline_applied_count ?? row?.applied_count, 0),
  matched: toNonNegativeNumber(
    row?.pipeline_matched_count ?? row?.matches_count ?? row?.matched_count,
    0
  ),
  sourced: toNonNegativeNumber(row?.pipeline_sourced_count ?? row?.sourced_count, 0),
  contacted: toNonNegativeNumber(row?.pipeline_contacted_count ?? row?.contacted_count, 0),
  screened: toNonNegativeNumber(row?.pipeline_screened_count ?? row?.screened_count, 0),
  presented: toNonNegativeNumber(row?.pipeline_presented_count ?? row?.presented_count, 0),
  interview: toNonNegativeNumber(
    row?.pipeline_interview_count ?? row?.interview_count ?? row?.interviewing_count,
    0
  ),
  assessment: toNonNegativeNumber(
    row?.pipeline_assessment_count ?? row?.assessment_count ?? row?.assessments_count,
    0
  ),
  offer: toNonNegativeNumber(row?.pipeline_offer_count ?? row?.offer_count ?? row?.offers_count, 0),
  hired: toNonNegativeNumber(row?.pipeline_hired_count ?? row?.hired_count ?? row?.hires_count, 0),
});

function JobAvatarStackCell({ people = [], emptyLabel = "-" }) {
  if (!people.length) {
    return (
      <Typography sx={{ fontSize: 14, color: "#98A2B3", fontFamily: "Be Vietnam Pro" }}>
        {emptyLabel}
      </Typography>
    );
  }

  const visiblePeople = people.slice(0, 3);
  const remainingCount = Math.max(0, people.length - visiblePeople.length);
  return (
    <Box
      title={people.map((item) => item?.name).filter(Boolean).join(", ") || emptyLabel}
      sx={{ display: "flex", alignItems: "center", minWidth: 0 }}
    >
      {visiblePeople.map((person, index) => (
        <Avatar
          key={`${person?.name || "person"}-${index}`}
          src={person?.avatar || undefined}
          sx={{
            width: 28,
            height: 28,
            ml: index === 0 ? 0 : -0.9,
            border: "2px solid #fff",
            fontSize: 11,
            fontWeight: 700,
            fontFamily: "Be Vietnam Pro",
            backgroundColor: index % 2 === 0 ? "#F8F1FF" : "#EEF2FF",
            color: index % 2 === 0 ? "#7C3AED" : "#3451F4",
            boxShadow: "0 0 0 1px rgba(152, 162, 179, 0.18)",
          }}
        >
          {!person?.avatar ? getAvatarLetters(person?.name, "NA") : null}
        </Avatar>
      ))}
      {remainingCount > 0 ? (
        <Typography
          sx={{
            ml: 1,
            fontSize: 12,
            fontWeight: 700,
            color: "#7C3AED",
            fontFamily: "Be Vietnam Pro",
          }}
        >
          +{remainingCount}
        </Typography>
      ) : null}
    </Box>
  );
}

const hasLoadedJobDetailFields = (job = {}) => {
  const description = String(job?.job_description || job?.description || "").trim();
  const responsibilities = String(job?.responsibilities || "").trim();
  const qualifications = String(job?.qualifications || "").trim();

  return Boolean(description && responsibilities && qualifications);
};

const createEmptyJobAdvancedFilters = () => ({
  job: { status: [], priority: [], department: [], employmentType: [], workArrangement: [] },
  client: { company: [], hiringManager: [] },
  team: { recruiter: false, sourcer: false, accountManager: false },
  pipeline: { hasCandidates: false, hasInterviews: false, hasOffers: false, hasHires: false },
  activity: { dateOpened: "", lastActivity: "", recentlyUpdated: false },
  attention: { noCandidates: false, noActivity: false, noRecruiter: false, noHiringManager: false, noPresentations: false, noInterviews: false },
  aiAgent: "",
  companyName: "",
  skills: [],
  locations: [],
  owner: [],
  status: [],
  activityTypes: [],
  source: [],
  company_details: {
    company_name: "",
    company_size: "",
    email: "",
    phone: "",
    industry: [],
    company_type: [],
    recruitment_tools: [],
    job_boards: [],
  },
  activity_section: {
    last_activity: "",
    tags: [],
    past_applications: [],
    notes: [],
  },
  jobs_section: {
    job_title: [],
    status: "",
    languages: [],
    industries: [],
  },
  contacts_section: {
    current_company: "",
    company_size: "",
    name: [],
    title: [],
    email: [],
    phone: [],
  },
});

const normalizeAdvancedList = (value) => uniq(Array.isArray(value) ? value : value ? [value] : []);
const normalizeJobLocationList = (value) =>
  normalizeLocationFilterValues(Array.isArray(value) ? value : value ? [value] : []);

const normalizeJobAdvancedFilters = (input) => {
  const empty = createEmptyJobAdvancedFilters();
  const next = input || {};
  const companyDetails = next.company_details || {};
  const activitySection = next.activity_section || {};
  const jobsSection = next.jobs_section || {};
  const contactsSection = next.contacts_section || {};
  const job = next.job || {};
  const client = next.client || {};
  const team = next.team || {};

  return {
    job: { ...empty.job, status: normalizeAdvancedList(job.status), priority: normalizeAdvancedList(job.priority), department: normalizeAdvancedList(job.department), employmentType: normalizeAdvancedList(job.employmentType), workArrangement: normalizeAdvancedList(job.workArrangement) },
    client: { ...empty.client, company: normalizeAdvancedList(client.company), hiringManager: normalizeAdvancedList(client.hiringManager) },
    team: { ...empty.team, recruiter: Boolean(team.recruiter), sourcer: Boolean(team.sourcer), accountManager: Boolean(team.accountManager) },
    pipeline: { ...empty.pipeline, ...(next.pipeline || {}) },
    activity: { ...empty.activity, ...(next.activity || {}), dateOpened: cleanText(next.activity?.dateOpened), lastActivity: cleanText(next.activity?.lastActivity) },
    attention: { ...empty.attention, ...(next.attention || {}) },
    aiAgent: cleanText(next.aiAgent),
    companyName: cleanText(next.companyName),
    skills: normalizeAdvancedList(next.skills),
    locations: normalizeJobLocationList(next.locations),
    owner: normalizeAdvancedList(next.owner),
    status: normalizeAdvancedList(next.status),
    activityTypes: normalizeAdvancedList(next.activityTypes),
    source: normalizeAdvancedList(next.source),
    company_details: {
      ...empty.company_details,
      company_name: cleanText(companyDetails.company_name),
      company_size: cleanText(companyDetails.company_size),
      email: cleanText(companyDetails.email),
      phone: cleanText(companyDetails.phone),
      industry: normalizeAdvancedList(companyDetails.industry),
      company_type: normalizeAdvancedList(companyDetails.company_type),
      recruitment_tools: normalizeAdvancedList(companyDetails.recruitment_tools),
      job_boards: normalizeAdvancedList(companyDetails.job_boards),
    },
    activity_section: {
      ...empty.activity_section,
      last_activity: cleanText(activitySection.last_activity),
      tags: normalizeAdvancedList(activitySection.tags),
      past_applications: normalizeAdvancedList(activitySection.past_applications),
      notes: normalizeAdvancedList(activitySection.notes),
    },
    jobs_section: {
      ...empty.jobs_section,
      job_title: normalizeAdvancedList(jobsSection.job_title),
      status: cleanText(jobsSection.status),
      languages: normalizeAdvancedList(jobsSection.languages),
      industries: normalizeAdvancedList(jobsSection.industries),
    },
    contacts_section: {
      ...empty.contacts_section,
      current_company: cleanText(contactsSection.current_company),
      company_size: cleanText(contactsSection.company_size),
      name: normalizeAdvancedList(contactsSection.name),
      title: normalizeAdvancedList(contactsSection.title),
      email: normalizeAdvancedList(contactsSection.email),
      phone: normalizeAdvancedList(contactsSection.phone),
    },
  };
};

const mergeBasicIntoAdvancedJobFilters = (basicFilters, currentAdvanced) => {
  const base = normalizeJobAdvancedFilters(currentAdvanced);
  const nextBasic = basicFilters || {};

  return {
    ...base,
    aiAgent: cleanText(nextBasic.ai_agent),
    skills: normalizeAdvancedList(nextBasic.tech_stacks),
    locations: normalizeJobLocationList(nextBasic.locations),
    owner: normalizeAdvancedList(nextBasic.owners),
    status: normalizeAdvancedList(nextBasic.statuses),
    activityTypes: normalizeAdvancedList(nextBasic.activities),
    source: normalizeAdvancedList(nextBasic.sources),
    jobs_section: {
      ...base.jobs_section,
      job_title: normalizeAdvancedList(nextBasic.job_titles),
      status:
        nextBasic.statuses?.length === 1
          ? cleanText(nextBasic.statuses[0])
          : "",
    },
  };
};

const advancedJobFiltersToBasicFilters = (advancedFilters, currentBasic) => {
  const base = currentBasic || {};
  const advanced = normalizeJobAdvancedFilters(advancedFilters);

  return {
    ...base,
    ai_agent: advanced.aiAgent,
    job_titles: normalizeAdvancedList(advanced.jobs_section.job_title),
    tech_stacks: normalizeAdvancedList(advanced.skills),
    locations: normalizeJobLocationList(advanced.locations),
    owners: normalizeAdvancedList(advanced.owner),
    statuses: normalizeAdvancedList([
      ...advanced.status,
      ...(advanced.jobs_section.status ? [advanced.jobs_section.status] : []),
    ]),
    activities: normalizeAdvancedList(advanced.activityTypes),
    sources: normalizeAdvancedList(advanced.source),
  };
};

const buildAdvancedJobQuery = (advancedFilters) => {
  if (!advancedFilters) return {};

  const advanced = normalizeJobAdvancedFilters(advancedFilters);

  return {
    advanced_status: advanced.job.status,
    advanced_priority: advanced.job.priority,
    advanced_department: advanced.job.department,
    advanced_employment_type: advanced.job.employmentType,
    advanced_work_arrangement: advanced.job.workArrangement,
    advanced_client_company: advanced.client.company,
    advanced_hiring_manager: advanced.client.hiringManager,
    advanced_has_recruiter: advanced.team.recruiter ? "1" : "",
    advanced_has_sourcer: advanced.team.sourcer ? "1" : "",
    advanced_has_account_manager: advanced.team.accountManager ? "1" : "",
    advanced_date_opened: advanced.activity.dateOpened,
    advanced_activity_search: advanced.activity.lastActivity,
    advanced_recently_updated: advanced.activity.recentlyUpdated ? "1" : "",
    advanced_has_candidates: advanced.pipeline.hasCandidates ? "1" : "",
    advanced_has_interviews: advanced.pipeline.hasInterviews ? "1" : "",
    advanced_has_offers: advanced.pipeline.hasOffers ? "1" : "",
    advanced_has_hires: advanced.pipeline.hasHires ? "1" : "",
    advanced_no_candidates: advanced.attention.noCandidates ? "1" : "",
    advanced_no_activity: advanced.attention.noActivity ? "1" : "",
    advanced_no_recruiter: advanced.attention.noRecruiter ? "1" : "",
    advanced_no_hiring_manager: advanced.attention.noHiringManager ? "1" : "",
    advanced_no_presentations: advanced.attention.noPresentations ? "1" : "",
    advanced_no_interviews: advanced.attention.noInterviews ? "1" : "",
    advanced_company_name: normalizeAdvancedList([
      advanced.companyName,
      advanced.company_details.company_name,
      advanced.contacts_section.current_company,
    ]),
    advanced_company_size: normalizeAdvancedList([
      advanced.company_details.company_size,
      advanced.contacts_section.company_size,
    ]),
    advanced_industry: normalizeAdvancedList([
      ...advanced.company_details.industry,
      ...advanced.jobs_section.industries,
    ]),
    advanced_company_type: normalizeAdvancedList(advanced.company_details.company_type),
    advanced_recruitment_tools: normalizeAdvancedList(advanced.company_details.recruitment_tools),
    advanced_job_boards: normalizeAdvancedList(advanced.company_details.job_boards),
    advanced_last_activity: advanced.activity_section.last_activity,
    advanced_tags: normalizeAdvancedList(advanced.activity_section.tags),
    advanced_past_applications: normalizeAdvancedList(advanced.activity_section.past_applications),
    advanced_notes: normalizeAdvancedList(advanced.activity_section.notes),
    advanced_languages: normalizeAdvancedList(advanced.jobs_section.languages),
    advanced_contact_name: normalizeAdvancedList(advanced.contacts_section.name),
    advanced_contact_title: normalizeAdvancedList(advanced.contacts_section.title),
    advanced_contact_email: normalizeAdvancedList([
      advanced.company_details.email,
      ...advanced.contacts_section.email,
    ]),
    advanced_contact_phone: normalizeAdvancedList([
      advanced.company_details.phone,
      ...advanced.contacts_section.phone,
    ]),
  };
};

const normalizeId = (v) => {
  if (v === null || v === undefined) return null;
  const s = String(v);
  const n = Number(s);
  return Number.isFinite(n) && String(n) === s ? n : s;
};

function formatCreatedShort(value) {
  return formatLocalDate(
    value,
    { weekday: "short", day: "numeric", month: "short" },
    "-"
  );
}

/* ---------------- Quick Job Detail Drawer (MATCH UPLOADED UI) ---------------- */
const ActionPill = ({ icon, onClick }) => (
  <IconButton
    className={withPillButtonClassName()}
    onClick={onClick}
    sx={[pillButtonInteractiveSx, iconPillButtonSx]}
  >
    {icon}
  </IconButton>
);

const QuickJobDetailDrawer = ({ open, onClose, job, onShowMore }) => {
  const [hiringManagers, setHiringManagers] = useState([]);
  const [hiringManagersLoading, setHiringManagersLoading] = useState(false);

  useEffect(() => {
    if (!open || !job?.id) {
      if (!open) {
        setHiringManagers([]);
        setHiringManagersLoading(false);
      }
      return undefined;
    }

    const controller = new AbortController();
    let active = true;

    setHiringManagers([]);
    setHiringManagersLoading(true);

    fetchHiringManagers({
      apiBase: API_BASE || "",
      entityType: "job",
      entityId: job.id,
      signal: controller.signal,
    })
      .then((rows) => {
        if (!active) return;
        setHiringManagers((Array.isArray(rows) ? rows : []).filter((row) => isHiringManagerContactRow(row)));
      })
      .catch((error) => {
        if (!active || error?.name === "AbortError") return;
        console.error("Failed to fetch job hiring managers:", error);
      })
      .finally(() => {
        if (active) setHiringManagersLoading(false);
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, [job?.id, open]);

  if (!job) return null;

  const PURPLE = "#9C4FFF";
  const PURPLE_DARK = "#6d28d9";
  const LABEL = "#3451F4";
  const BORDER = "#E5E7EB";
  const TEXT_MUTED = "#94A3B8";

  const companyLogo = job.company_logo || "";
  const createdAt = formatCreatedShort(job.created_at);

  const lastActivityDate = job.last_activity_time || job.last_activity_date || "";
  const lastActivityLabel = formatRelativeTimeShort(lastActivityDate, { emptyValue: "-" });

  const source = job.source || "LinkedIn";
  const displayCompanyName = job.company_name || "-";
  const displayCompanyWebsite = job.company_website || job.website || "-";
  const displayIndustry =
    job.company_industry || job.industry_sector || job.industry || "-";
  const displayOwner = getRecruiterOwnerName(job) || "-";
  const fallbackHiringManagerName = getDistinctJobHiringManagerName(job);
  const visibleHiringManagers = dedupeHiringManagerRows(
    hiringManagers.filter((item) => {
      const managerName = cleanText(item?.name || item?.contact_name || item?.hiring_manager_name);
      if (!managerName) return false;
      if (!isHiringManagerContactRow(item)) return false;
      if (isRecruiterContact(item)) return false;
      return !areSamePersonNames(managerName, displayOwner);
    })
  );
  const displayHiringManager =
    visibleHiringManagers[0]?.name || fallbackHiringManagerName || "-";
  const displayLastActivityLine1 =
    job.last_activity_email ||
    job.last_activity_actor ||
    (job.status ? `Status: ${job.status}` : "Job record updated");
  const displayLastActivityLine2 =
    job.last_activity_subject ||
    job.last_activity_detail ||
    (job.source
      ? `Source: ${job.source}`
      : job.notes || "No recent activity details");
  const displayJobsCount = Math.max(1, Number(job.jobs_count || job.jobsCount || 1));
  const displayJobsList =
    Array.isArray(job.related_jobs) && job.related_jobs.length
      ? job.related_jobs.slice(0, 2).map((x) => ({
          title: x.title || x.job_title || "-",
          location: x.location || "",
        }))
      : [];
  const derivedContacts = [
    ...(displayHiringManager && displayHiringManager !== "-"
      ? [{ name: displayHiringManager, role: "Hiring Manager" }]
      : []),
  ].slice(0, 2);
  const sanitizedDrawerContacts = Array.isArray(job.contacts)
    ? job.contacts.filter((contact) => {
        const role = cleanText(contact?.role).toLowerCase();
        const name = cleanText(contact?.name || contact?.contact_name || contact?.hiring_manager_name);
        if (!name) return false;
      if (isRecruiterContact(contact)) return false;
      if (/owner|recruiter/i.test(role)) return false;
        return !areSamePersonNames(name, displayOwner);
      })
    : [];
  const displayContacts =
    sanitizedDrawerContacts.length
      ? sanitizedDrawerContacts.slice(0, 2).map((c) => ({
          name: c.name || c.full_name || "-",
          role: c.role || c.title || "-",
        }))
      : derivedContacts;
  const fallbackHiringManagers = dedupeHiringManagerRows(
    fallbackHiringManagerName && fallbackHiringManagerName !== "-"
      ? [{ name: displayHiringManager, role: "Hiring Manager" }]
      : []
  );
  const hiringManagersPreview = (
    visibleHiringManagers.length ? visibleHiringManagers : fallbackHiringManagers
  ).slice(0, 3);

  const headerLeft = (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
      <Box
        sx={{
          width: 26,
          height: 26,
          borderRadius: "999px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#EEF2FF",
          color: PURPLE,
          flex: "0 0 auto",
        }}
      >
        <Eye size="16" color={PURPLE} variant="Linear" />
      </Box>

      <Typography
        sx={{
          fontSize: 12,
          fontWeight: 800,
          color: LABEL,
          fontFamily: "Be Vietnam Pro",
        }}
        noWrap
      >
        Quick Job Detail
      </Typography>
    </Box>
  );

  const headerRight = (
    <IconButton data-popup-close
      onClick={onClose}
      size="small"
      sx={{
        width: 26,
        height: 26,
        borderRadius: "999px",
        backgroundColor: "#EEF2FF",
        color: PURPLE,
        "&:hover": { backgroundColor: "#E0E7FF" },
      }}
    >
      {/* FIX: heavier + slightly larger + block SVG so it never "disappears" */}
      <CloseCircle size="18" color={PURPLE} variant="Bold" style={{ display: "block" }} />
    </IconButton>
  );

  const footer = (
    <Box sx={{ py: 1 }}>
      <Button
        onClick={() => onShowMore?.(job)}
        variant="contained"
        sx={{
          display: "block",
          mx: "auto",
          borderRadius: 999,
          textTransform: "none",
          fontWeight: 800,
          fontSize: 12,
          fontFamily: "Be Vietnam Pro",
          backgroundColor: PURPLE,
          boxShadow: "none",
          px: 4,
          "&:hover": { backgroundColor: PURPLE_DARK, boxShadow: "none" },
        }}
      >
        Show more
      </Button>
    </Box>
  );

  return (
    <ReusableRightDrawer
      open={open}
      onClose={onClose}
      id="quick-job-detail-drawer"
      width={380}
      headerLeft={headerLeft}
      headerRight={headerRight}
      closeVariant="text"
      closeColor={PURPLE}
      paperSx={{
        borderRadius: "14px",
        boxShadow: "0 18px 45px rgba(2, 6, 23, 0.22)",
      }}
      footer={footer}
    >
      <Typography sx={{ fontWeight: 800, fontSize: 16, fontFamily: "Be Vietnam Pro", mb: 1.4 }}>
        {job.job_title || "-"}
      </Typography>

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1.25 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 12, color: LABEL, fontWeight: 800, fontFamily: "Be Vietnam Pro", mb: 0.5 }}>
            Hiring Manager:
            <Box component="span" sx={{ ml: 0.75, textDecoration: "underline", cursor: "pointer" }}>
              {displayHiringManager}
            </Box>
          </Typography>
        </Box>

        <Box sx={{ textAlign: "right" }}>
          <Stack direction="row" justifyContent="flex-end" alignItems="center" gap={0.75}>
            <Typography sx={{ fontSize: 11, color: "#111827", fontFamily: "Be Vietnam Pro" }}>Owner:</Typography>
            <Chip
              label={displayOwner}
              size="small"
              sx={{
                height: 22,
                borderRadius: 999,
                fontWeight: 800,
                fontSize: 11,
                fontFamily: "Be Vietnam Pro",
                border: `1px solid ${BORDER}`,
                backgroundColor: "#fff",
              }}
            />
          </Stack>

          <Typography sx={{ fontSize: 10, color: TEXT_MUTED, fontFamily: "Be Vietnam Pro", mt: 0.6 }}>
            Created: {createdAt}
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ borderColor: "#EEF2F7", my: 1.4 }} />

      <Typography sx={{ fontSize: 12, fontWeight: 900, color: LABEL, fontFamily: "Be Vietnam Pro", mb: 1 }}>
        Company:
      </Typography>

      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 1.6 }}>
        <Avatar
          src={/^(null|undefined|false)$/i.test(companyLogo) ? undefined : (companyLogo || undefined)}
          sx={{
            width: 38,
            height: 38,
            background: "radial-gradient(circle at 30% 30%, #a855f7, #6366f1)",
            color: "#fff",
          }}
        >
          {(job.company_name || "C")[0]}
        </Avatar>

        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 900, fontSize: 13, fontFamily: "Be Vietnam Pro" }} noWrap>
            {displayCompanyName}
          </Typography>
          <Typography sx={{ fontSize: 12, color: PURPLE, fontFamily: "Be Vietnam Pro" }} noWrap>
            {displayCompanyWebsite}
          </Typography>
          <Typography sx={{ fontSize: 11, color: TEXT_MUTED, fontFamily: "Be Vietnam Pro" }} noWrap>
            {displayIndustry}
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ borderColor: "#EEF2F7", my: 1.4 }} />

      <Typography sx={{ fontSize: 12, fontWeight: 900, color: LABEL, fontFamily: "Be Vietnam Pro", mb: 1 }}>
        Quick Actions:
      </Typography>

      <Stack direction="row" spacing={1.25} sx={{ mb: 1.6 }}>
        <ActionPill icon={<Call size="16" color="#fff" variant="Outline" />} onClick={() => {}} />
        <ActionPill icon={<Sms size="16" color="#fff" variant="Outline" />} onClick={() => {}} />
        <ActionPill icon={<Calendar1 size="16" color="#fff" variant="Outline" />} onClick={() => {}} />
        <ActionPill icon={<People size="16" color="#fff" variant="Outline" />} onClick={() => {}} />
      </Stack>

      <Divider sx={{ borderColor: "#EEF2F7", my: 1.4 }} />

      <Typography sx={{ fontSize: 12, fontWeight: 900, color: LABEL, fontFamily: "Be Vietnam Pro", mb: 1 }}>
        Last Activity:
      </Typography>

      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.75 }}>
        <Clock size="16" color={TEXT_MUTED} variant="Linear" />
        <Typography sx={{ fontSize: 11, color: TEXT_MUTED, fontFamily: "Be Vietnam Pro" }}>{lastActivityLabel}</Typography>
      </Box>

      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 11, color: "#111827", fontFamily: "Be Vietnam Pro" }} noWrap>
            {displayLastActivityLine1}
          </Typography>
          <Typography sx={{ fontSize: 11, color: TEXT_MUTED, fontFamily: "Be Vietnam Pro" }} noWrap>
            {displayLastActivityLine2}
          </Typography>
        </Box>

        <Typography
          sx={{
            fontSize: 11,
            color: PURPLE,
            fontWeight: 800,
            fontFamily: "Be Vietnam Pro",
            cursor: "pointer",
            whiteSpace: "nowrap",
          }}
          onClick={() => {}}
        >
          View
        </Typography>
      </Box>

      <Divider sx={{ borderColor: "#EEF2F7", my: 1.4 }} />

      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.8 }}>
        <Typography sx={{ fontSize: 12, fontWeight: 900, color: LABEL, fontFamily: "Be Vietnam Pro" }}>
          Jobs ({displayJobsCount}):
        </Typography>

        <Typography sx={{ fontSize: 11, color: PURPLE, fontWeight: 800, fontFamily: "Be Vietnam Pro", cursor: "pointer" }}>
          View all
        </Typography>
      </Box>

      <Box sx={{ mb: 1.2 }}>
        {displayJobsList.length ? (
          displayJobsList.map((j, idx) => (
            <Box key={idx} sx={{ mb: 0.6 }}>
              <Typography sx={{ fontSize: 11, color: "#111827", fontFamily: "Be Vietnam Pro" }}>
                {j.title}
                {j.location ? ` - ${j.location}` : ""}
              </Typography>
            </Box>
          ))
        ) : (
          <Typography sx={{ fontSize: 11, color: TEXT_MUTED, fontFamily: "Be Vietnam Pro" }}>
            -
          </Typography>
        )}
      </Box>

      <Divider sx={{ borderColor: "#EEF2F7", my: 1.4 }} />

      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.8 }}>
        <Typography sx={{ fontSize: 12, fontWeight: 900, color: LABEL, fontFamily: "Be Vietnam Pro" }}>Contacts</Typography>
        <Typography sx={{ fontSize: 11, color: PURPLE, fontWeight: 800, fontFamily: "Be Vietnam Pro", cursor: "pointer" }}>
          View all
        </Typography>
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: "1.3fr 1fr 44px", gap: 1, mb: 0.6 }}>
        <Typography sx={{ fontSize: 10, color: TEXT_MUTED, fontFamily: "Be Vietnam Pro" }}>Name</Typography>
        <Typography sx={{ fontSize: 10, color: TEXT_MUTED, fontFamily: "Be Vietnam Pro" }}>Role</Typography>
        <Typography sx={{ fontSize: 10, color: TEXT_MUTED, fontFamily: "Be Vietnam Pro", textAlign: "right" }}>Actions</Typography>
      </Box>

      {(displayContacts.length ? displayContacts : [{ name: "-", role: "-" }]).map((c, idx) => (
        <Box
          key={idx}
          sx={{
            display: "grid",
            gridTemplateColumns: "1.3fr 1fr 44px",
            gap: 1,
            alignItems: "center",
            py: 0.75,
            borderTop: idx === 0 ? `1px solid ${BORDER}` : "none",
            borderBottom: `1px solid ${BORDER}`,
          }}
        >
          <Typography sx={{ fontSize: 11, color: "#111827", fontFamily: "Be Vietnam Pro" }} noWrap>
            {c.name}
          </Typography>
          <Typography sx={{ fontSize: 11, color: TEXT_MUTED, fontFamily: "Be Vietnam Pro" }} noWrap>
            {c.role}
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <IconButton size="small" sx={{ color: PURPLE }} disabled={c.name === "-"}>
              <Trash size="16" color={PURPLE} variant="Linear" />
            </IconButton>
          </Box>
        </Box>
      ))}

      <Divider sx={{ borderColor: "#EEF2F7", my: 1.4 }} />

      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.8 }}>
        <Typography sx={{ fontSize: 12, fontWeight: 900, color: LABEL, fontFamily: "Be Vietnam Pro" }}>
          Hiring Managers
        </Typography>
        <Typography
          sx={{ fontSize: 11, color: PURPLE, fontWeight: 800, fontFamily: "Be Vietnam Pro", cursor: "pointer" }}
          onClick={() => onShowMore?.(job)}
        >
          View all
        </Typography>
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: "1.3fr 1fr", gap: 1, mb: 0.6 }}>
        <Typography sx={{ fontSize: 10, color: TEXT_MUTED, fontFamily: "Be Vietnam Pro" }}>Name</Typography>
        <Typography sx={{ fontSize: 10, color: TEXT_MUTED, fontFamily: "Be Vietnam Pro" }}>Role</Typography>
      </Box>

      {hiringManagersLoading ? (
        <Typography sx={{ fontSize: 11, color: TEXT_MUTED, fontFamily: "Be Vietnam Pro", py: 0.75 }}>
          Loading hiring managers...
        </Typography>
      ) : hiringManagersPreview.length ? (
        hiringManagersPreview.map((manager, idx) => (
          <Box
            key={`${manager.id ?? manager.name}-${idx}`}
            sx={{
              display: "grid",
              gridTemplateColumns: "1.3fr 1fr",
              gap: 1,
              alignItems: "center",
              py: 0.75,
              borderTop: idx === 0 ? `1px solid ${BORDER}` : "none",
              borderBottom: `1px solid ${BORDER}`,
            }}
          >
            <Typography sx={{ fontSize: 11, color: "#111827", fontFamily: "Be Vietnam Pro" }} noWrap>
              {manager.name}
            </Typography>
            <Typography sx={{ fontSize: 11, color: TEXT_MUTED, fontFamily: "Be Vietnam Pro" }} noWrap>
              {manager.role}
            </Typography>
          </Box>
        ))
      ) : (
        <Typography sx={{ fontSize: 11, color: TEXT_MUTED, fontFamily: "Be Vietnam Pro", py: 0.75 }}>
          No hiring managers added yet.
        </Typography>
      )}

      <Divider sx={{ borderColor: "#EEF2F7", my: 1.4 }} />

      <Typography sx={{ fontSize: 11, color: TEXT_MUTED, fontFamily: "Be Vietnam Pro", mb: 0.6 }}>Source</Typography>
      <TextField
        fullWidth
        size="small"
        value={source}
        InputProps={{ readOnly: true }}
        sx={{
          "& .MuiOutlinedInput-root": {
            borderRadius: "8px",
            backgroundColor: "#fff",
            fontSize: 12,
            "& fieldset": { borderColor: BORDER },
          },
          "& input": { fontSize: 12, fontFamily: "Be Vietnam Pro" },
        }}
      />
    </ReusableRightDrawer>
  );
};

/* ---------------- Filter Drawer (ReusableRightDrawer + sticky bottom buttons) ---------------- */
const FilterPopup = ({
  open,
  onClose,
  draft,
  setDraft,
  endpoint = "/api/jobs",
  onClear,
  onApply,
  onAdvancedSearch,
}) => {

  const directory = usePageFilterOptions(open, endpoint, draft, setDraft);
  const options = directory.options;
  const PURPLE = "#9C4FFF";
  const PURPLE_DARK = "#9C4FFF";
  const LABEL_BLUE = "#3451F4";

  const mergedOptions = useMemo(() => {
    return {
      job_titles: options.job_titles || [],
      tech_stacks: options.tech_stacks || [],
      locations: normalizeJobLocationList(options.locations || []),
      owners: options.owners || [],
      statuses: options.statuses || [],
      activities: options.activities || [],
      sources: options.sources || [],
    };
  }, [options]);

  const sectionLabelSx = {
    fontSize: 14,
    fontWeight: 700,
    color: LABEL_BLUE,
    mb: 0.9,
    fontFamily: "Be Vietnam Pro",
  };

  const inputSx = popupFilterInputSx(PURPLE);

  const dividerSx = { my: 1.6, borderColor: "#eef2f7" };

  const setField = (key, value) => setDraft((p) => ({ ...p, [key]: value }));

  const headerLeft = (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Box
        sx={{
          width: 26,
          height: 26,
          borderRadius: "999px",
          border: `2px solid ${PURPLE}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#fff",
        }}
      >
        <Filter size="16" color={PURPLE} variant="Linear" />
      </Box>

      <Typography sx={{ fontSize: 14, fontWeight: 800, color: LABEL_BLUE, fontFamily: "Be Vietnam Pro" }}>
        Filter by
      </Typography>
    </Box>
  );

  return (
    <ReusableRightDrawer
      open={open}
      onClose={onClose}
      id="jobs-filter-drawer"
      width={546}
      headerLeft={headerLeft}
      closeVariant="contained"
      closeColor={PURPLE}
      paperSx={{
        borderRadius: "14px",
        boxShadow: "0 18px 45px rgba(2, 6, 23, 0.22)",
        overflow: "hidden",
      }}
      footer={null}
    >
      {/* ...rest of your FilterPopup stays exactly the same... */}
      {/* (Keeping as-is; no icon changes needed here.) */}
      <Box sx={{ pb: 10 }}>
        {directory.loading && <Typography role="status">Loading all filter values...</Typography>}
        {directory.error && <Alert severity="error">{directory.error}</Alert>}
        <Typography sx={sectionLabelSx}>AI Agent:</Typography>
        <TextField
          fullWidth
          size="small"
          placeholder="Type your request"
          value={draft.ai_agent || ""}
          onChange={(e) => setField("ai_agent", e.target.value)}
          sx={inputSx}
        />

        <Divider sx={dividerSx} />

        <Typography sx={sectionLabelSx}>Job Title:</Typography>
        <PopupMultiSelectField
          freeSolo={false}
          options={mergedOptions.job_titles}
          {...directory.fieldProps("job_titles")}
          value={draft.job_titles || []}
          onChange={(next) => setField("job_titles", uniq(next))}
          placeholder="Type Job Title"
          inputSx={inputSx}
        />

        <Divider sx={dividerSx} />

        <Typography sx={sectionLabelSx}>Skills:</Typography>
        <PopupMultiSelectField
          freeSolo={false}
          options={mergedOptions.tech_stacks}
          {...directory.fieldProps("tech_stacks")}
          value={draft.tech_stacks || []}
          onChange={(next) => setField("tech_stacks", uniq(next))}
          placeholder="Tech Stack keyword anywhere on profile"
          inputSx={inputSx}
        />

        <Divider sx={dividerSx} />

        <Typography sx={sectionLabelSx}>Locations:</Typography>
        <LocationMultiSelectField
          freeSolo={false}
          searchExternal={false}
          options={mergedOptions.locations}
          {...directory.fieldProps("locations")}
          value={draft.locations || []}
          onChange={(next) => setField("locations", normalizeJobLocationList(next))}
          placeholder="Select location"
          inputSx={inputSx}
          autocompleteProps={{ ...directory.fieldProps("locations").autocompleteProps, groupBy: groupLocationOption }}
        />

        <Divider sx={dividerSx} />

        <Typography sx={sectionLabelSx}>Owner:</Typography>
        <PopupMultiSelectField
          freeSolo={false}
          options={mergedOptions.owners}
          {...directory.fieldProps("owners")}
          value={draft.owners || []}
          onChange={(next) => setField("owners", uniq(next))}
          placeholder="Select the owner"
          inputSx={inputSx}
        />

        <Divider sx={dividerSx} />

        <Typography sx={sectionLabelSx}>Status:</Typography>
        <PopupMultiSelectField
          freeSolo={false}
          options={mergedOptions.statuses}
          {...directory.fieldProps("statuses")}
          value={draft.statuses || []}
          onChange={(next) => setField("statuses", uniq(next))}
          placeholder="Select one or more Status"
          inputSx={inputSx}
        />

        <Divider sx={dividerSx} />

        <Typography sx={sectionLabelSx}>Activity:</Typography>
        <PopupMultiSelectField
          freeSolo={false}
          options={mergedOptions.activities}
          {...directory.fieldProps("activities")}
          value={draft.activities || []}
          onChange={(next) => setField("activities", uniq(next))}
          placeholder="Select one or more activities"
          inputSx={inputSx}
        />

        <Divider sx={dividerSx} />

        <Typography sx={sectionLabelSx}>Source:</Typography>
        <PopupMultiSelectField
          freeSolo={false}
          options={mergedOptions.sources}
          {...directory.fieldProps("sources")}
          value={draft.sources || []}
          onChange={(next) => setField("sources", uniq(next))}
          placeholder="Select one or more Sources"
          inputSx={inputSx}
        />
      </Box>

      <Box
        sx={{
          position: "sticky",
          bottom: 0,
          zIndex: 10,
          backgroundColor: "#fff",
          borderTop: "1px solid #eef2f7",
          px: 2,
          py: 1.6,
        }}
      >
        <Stack direction="row" spacing={1.25} alignItems="center">
          {typeof onAdvancedSearch === "function" ? (
            <Button
              variant="contained"
              className={withPillButtonClassName()}
              sx={{
                borderRadius: 999,
                textTransform: "none",
                fontWeight: 400,
                fontSize: 12,
                fontFamily: "Be Vietnam Pro",
                backgroundColor: PURPLE,
                boxShadow: "none",
                px: 2.4,
                "&:hover": { backgroundColor: PURPLE_DARK, boxShadow: "none" },
              }}
              onClick={() => onAdvancedSearch(draft)}
            >
              Advance Search
            </Button>
          ) : null}

          <Box sx={{ flex: 1 }} />

          <Button
            variant="outlined"
            className={withPillButtonClassName()}
            sx={{
              borderRadius: 999,
              textTransform: "none",
              fontWeight: 400,
              fontSize: 12,
              fontFamily: "Be Vietnam Pro",
              borderColor: PURPLE,
              color: PURPLE,
              "&:hover": { borderColor: PURPLE_DARK, color: PURPLE_DARK, backgroundColor: "transparent" },
            }}
            onClick={onClear}
          >
            Clear Filters
          </Button>

          <Button
            variant="contained"
            className={withPillButtonClassName()}
            sx={{
              borderRadius: 999,
              textTransform: "none",
              fontWeight: 400,
              fontSize: 12,
              fontFamily: "Be Vietnam Pro",
              backgroundColor: PURPLE,
              boxShadow: "none",
              "&:hover": { backgroundColor: PURPLE_DARK, boxShadow: "none" },
            }}
            onClick={onApply}
          >
            Apply Filters
          </Button>
        </Stack>
      </Box>
    </ReusableRightDrawer>
  );
};

/* ---------------- MAIN COMPONENT ---------------- */
const JobsContent = ({ onDetailOpen, onDetailClose, user }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const navigationType = useNavigationType();
  const [jobData, setJobData] = useState([]);
  const [jobsTotal, setJobsTotal] = useState(0);
  const [allJobsTotal, setAllJobsTotal] = useState(0);
  const [pipelineStats, setPipelineStats] = useState({
    open_jobs: 0,
    assigned_recruiters: 0,
    applied: 0,
    matched: 0,
    sourced: 0,
    contacted: 0,
    screened: 0,
    presented: 0,
    interviewing: 0,
    assessments: 0,
    offers: 0,
    hires: 0,
    needing_attention: 0,
  });
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [statusToast, setStatusToast] = useState({
    open: false,
    severity: "success",
    message: "",
  });
  const [addJobOpen, setAddJobOpen] = useState(false);
  const [csvImportOpen, setCsvImportOpen] = useCsvImport();
  const [reloadKey, setReloadKey] = useState(0);
  useEffect(() => {
    const refresh = () => setReloadKey(value => value + 1);
    window.addEventListener("rpo:activities-refresh", refresh);
    return () => window.removeEventListener("rpo:activities-refresh", refresh);
  }, []);


  useEffect(() => {
    const handleJobCreated = () => {
      setPage(1);
      setReloadKey((prev) => prev + 1);
    };

    window.addEventListener("job-created", handleJobCreated);
    return () => window.removeEventListener("job-created", handleJobCreated);
  }, []);

  // Quick job detail popup
  const [quickOpen, setQuickOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);

  // Full detail view (inside JobBoardTable)
  const [detailJob, setDetailJob] = useState(null);
  const [rowMenuAnchorEl, setRowMenuAnchorEl] = useState(null);
  const [rowMenuItem, setRowMenuItem] = useState(null);

  const [search, setSearch] = useState("");
  const handleJobPageChange = useCallback((nextPage) => {
    setPage(Math.max(1, Number(nextPage) + 1));
  }, []);

  const handleJobRowsPerPageChange = useCallback((nextRowsPerPage) => {
    setRowsPerPage(Math.max(1, Number(nextRowsPerPage) || 10));
  }, []);

  // Selection
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectedIdsLocal, setSelectedIdsLocal] = useState([]);
  const [selectedContactIds, setSelectedContactIds] = useState([]);
  const [activeStatId, setActiveStatId] = useState("total");
  const [isAddToSequenceOpen, setIsAddToSequenceOpen] = useState(false);
  const [deleteSaving, setDeleteSaving] = useState(false);
  const [matchingCandidates, setMatchingCandidates] = useState(false);
  const effectiveSelectedIds = selectedIdsLocal.length ? selectedIdsLocal : selectedIds;
  const selectedIdSet = useMemo(
    () => new Set((effectiveSelectedIds || []).map((id) => String(id))),
    [effectiveSelectedIds]
  );
  const filterJobRows = useCallback((jobData) => {
    if (!activeStatId || activeStatId === "total") return jobData;

    const hasAssignedRecruitingCoverage = (row = {}) => {
      const recruiterName = cleanText(
        row?.owner || row?.assigned_team_member || row?.owner_name || row?.recruiter_name
      );
      return Boolean(recruiterName);
    };

    const matchers = {
      assignedRecruiters: (row) =>
        normalizeStatValue(normalizeJobStatusDisplay(row?.status)) === "open" &&
        hasAssignedRecruitingCoverage(row),
      applied: (row) => getJobPipelineSummary(row).applied > 0,
      matched: (row) => getJobPipelineSummary(row).matched > 0,
      sourced: (row) => getJobPipelineSummary(row).sourced > 0,
      contacted: (row) => getJobPipelineSummary(row).contacted > 0,
      screened: (row) => getJobPipelineSummary(row).screened > 0,
      presented: (row) => getJobPipelineSummary(row).presented > 0,
      interviewing: (row) => getJobPipelineSummary(row).interview > 0,
      assessments: (row) => getJobPipelineSummary(row).assessment > 0,
      offers: (row) => getJobPipelineSummary(row).offer > 0,
      hires: (row) => getJobPipelineSummary(row).hired > 0,
    };
    const dynamicStatusId = activeStatId.startsWith("status:")
      ? activeStatId.slice(7)
      : "";
    const predicate = dynamicStatusId
      ? (row) => normalizeStatValue(normalizeJobStatusDisplay(row?.status)) === dynamicStatusId
      : matchers[activeStatId];
    if (!predicate) return jobData;
    return jobData.filter((row) => predicate(row));
  }, [activeStatId]);

  const filteredJobRows = useMemo(() => filterJobRows(jobData), [filterJobRows, jobData]);

  // Preserve job enrichment work across route changes, including navigation-menu
  // clicks that unmount this table before its next refresh cycle.
  useEffect(() => {
    jobData.forEach((job) => syncJobBackgroundWork(job));
  }, [jobData]);
  const selectedJobRows = useMemo(
    () => filteredJobRows.filter((row) => selectedIdSet.has(String(row?.id))),
    [filteredJobRows, selectedIdSet]
  );
  const selectedSequenceItems = useMemo(
    () => selectedJobRows.map((row) => buildJobSequenceItem(row, "jobs")),
    [selectedJobRows]
  );
  const selectedCount = selectedJobRows.length;

  // Filters popup
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const jobStatusOptions = useMemo(
    () => JOB_STATUS_OPTIONS,
    []
  );

  const emptyFilters = useMemo(
    () => ({
      ai_agent: "",
      job_titles: [],
      tech_stacks: [],
      locations: [],
      owners: [],
      statuses: [],
      activities: [],
      sources: [],
    }),
    []
  );

  const [appliedFilters, setAppliedFilters] = useState(emptyFilters);
  const [contactFilters, setContactFilters] = useState({});
  const [draftFilters, setDraftFilters] = useState(emptyFilters);
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const [advancedApplied, setAdvancedApplied] = useState(null);
  const filterEndpoint = `/api/jobs${toQuery({
    search, stats_filter: activeStatId || "",
    candidate_stage: JOB_PIPELINE_STAGE_FILTER_VALUES_BY_STAT_ID[activeStatId] || [],
    ...buildAdvancedJobQuery(advancedApplied),
  })}`;
  const filterRecords = jobData;
  const filterOptions = useMemo(() => ({
    job_titles: uniq(filterRecords.map((row) => row.job_title || row.title)),
    tech_stacks: buildDetailFieldOptions(filterRecords).skills,
    locations: buildDetailFieldOptions(filterRecords).locations,
    owners: uniq(filterRecords.map(getSavedJobOwnerDisplay)),
    hiring_manager_names: uniq(filterRecords.map((row) => getJobHiringManagerDisplay(row).name)),
    statuses: uniq(filterRecords.map((row) => normalizeJobStatusDisplay(row.status))),
    activities: uniq(filterRecords.map((row) => row.last_activity_type || row.lastActivityType || row.last_activity)),
    sources: uniq(filterRecords.map((row) => row.source)),
  }), [filterRecords]);
  const overviewMetric = useMemo(
    () => new URLSearchParams(location.search).get("overviewMetric") || "",
    [location.search]
  );

  useEffect(() => {
    if (overviewMetric !== "open_jobs") return;
    const openJobsFilters = { ...emptyFilters, statuses: ["Open"] };
    setDraftFilters(openJobsFilters);
    setAppliedFilters(openJobsFilters);
    setActiveStatId("status:open");
    setPage(1);
  }, [emptyFilters, overviewMetric]);
  const sidebarResetToken = getSidebarResetToken(location, "/jobs");
  const detailRouteSearch = sidebarResetToken ? "" : location.search;
  const detailRouteTab = useMemo(
    () => normalizeJobRouteTab(new URLSearchParams(detailRouteSearch).get("tab")),
    [detailRouteSearch]
  );
  const detailRouteJobId = useMemo(
    () => String(new URLSearchParams(detailRouteSearch).get("jobId") || "").trim(),
    [detailRouteSearch]
  );
  const isDetailRouteActive = Boolean(detailRouteJobId && detailRouteTab);
  const wasDetailRouteActiveRef = useRef(isDetailRouteActive);

  useEffect(() => {
    const returnedToListWithBrowserBack =
      navigationType === "POP" && wasDetailRouteActiveRef.current && !isDetailRouteActive;
    wasDetailRouteActiveRef.current = isDetailRouteActive;

    if (!returnedToListWithBrowserBack) return;

    setSearch("");
    setDraftFilters(emptyFilters);
    setAppliedFilters(emptyFilters);
    setAdvancedApplied(null);
    setActiveStatId("total");
    setPage(1);
  }, [emptyFilters, isDetailRouteActive, navigationType]);

  const [listTab, setListTab] = useState(() =>
    new URLSearchParams(location.search).get("view") === "recruiting-pipeline"
      ? "recruiting-pipeline"
      : "jobs"
  );
  const showingContacts = listTab === "contacts";
  const showingRecruitingPipeline = listTab === "recruiting-pipeline";
  const [contactRows, setContactRows] = useState([]);
  const [contactRowsTotal, setContactRowsTotal] = useState(null);
  const [contactTableTotal, setContactTableTotal] = useState(0);
  const [contactTablePage, setContactTablePage] = useState(1);
  const [contactTableRowsPerPage, setContactTableRowsPerPage] = useState(10);
  const [recruitingPipelineRows, setRecruitingPipelineRows] = useState([]);
  const [recruitingStageStats, setRecruitingStageStats] = useState({});
  const [recruitingPipelineFilteredTotal, setRecruitingPipelineFilteredTotal] = useState(0);
  const [recruitingPipelineTotal, setRecruitingPipelineTotal] = useState(null);
  const [recruitingPipelinePage, setRecruitingPipelinePage] = useState(1);
  const [recruitingPipelineRowsPerPage, setRecruitingPipelineRowsPerPage] = useState(10);
  const [recruitingPipelineLoading, setRecruitingPipelineLoading] = useState(false);
  const [recruitingPipelineStage, setRecruitingPipelineStage] = useState(() =>
    String(new URLSearchParams(location.search).get("stage") || "").trim()
  );

  const pendingRefreshTimeoutRef = useRef(null);
  const pendingJobEnrichmentRefreshTimeoutRef = useRef(null);
  const pendingImportedJobIdsRef = useRef([]);
  const pendingImportRefreshAttemptsRef = useRef(0);
  const jobsRequestIdRef = useRef(0);

  useEffect(() => {
    if (!sidebarResetToken) return;

    setShowAdvancedSearch(false);
    setQuickOpen(false);
    setSelectedJob(null);
    setDetailJob(null);
    setRowMenuAnchorEl(null);
    setRowMenuItem(null);
    setActiveStatId("total");
    setListTab("jobs");
    onDetailClose?.();
    if (location.search) {
      navigate(location.pathname, { replace: true });
    }
  }, [location.pathname, location.search, navigate, onDetailClose, sidebarResetToken]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("view") !== "recruiting-pipeline") return;
    setListTab("recruiting-pipeline");
    setRecruitingPipelineStage(String(params.get("stage") || "").trim());
  }, [location.search]);

  useEffect(() => {
    setActiveStatId(
      listTab === "jobs" && overviewMetric === "open_jobs"
        ? "status:open"
        : "total",
    );
  }, [listTab, overviewMetric]);

  const advancedSearchOptions = useMemo(
    () => ({
      skills: filterOptions.tech_stacks,
      locations: filterOptions.locations,
      owners: uniq(filterOptions.owners || []),
      statuses: JOB_STATUS_OPTIONS,
      priorities: uniq(jobData.map((row) => row.priority || row.job_priority || row.priority_level).filter(Boolean)),
      departments: uniq(jobData.map((row) => row.department).filter(Boolean)),
      employmentTypes: uniq(jobData.map((row) => row.employment_type || row.employment).filter(Boolean)),
      companies: uniq(jobData.map((row) => row.company_name || row.linked_company_name).filter(Boolean)),
      hiringManagers: uniq(jobData.map((row) => row.hiring_manager_name || row.hiring_manager || row.contact_name).filter(Boolean)),
      recruiters: uniq(jobData.map((row) => row.owner || row.recruiter_name || row.assigned_team_member).filter(Boolean)),
      sourcers: uniq(jobData.map((row) => row.sourcer_name || row.sourcer).filter(Boolean)),
      accountManagers: uniq(jobData.map((row) => row.account_manager_name || row.account_manager).filter(Boolean)),
      activities: filterOptions.activities,
      sources: filterOptions.sources,
      jobTitles: filterOptions.job_titles,
      industries: normalizeIndustrySelectionValues(
        jobData.map(
          (row) =>
            row.industry_sectors_json ||
            row.company_industry ||
            row.industry ||
            row.industry_sector
        )
      ),
      contactNames: uniq(
        jobData.map((row) => row.hiring_manager_name || row.hiring_manager || row.contact_name).filter(Boolean)
      ),
      contactEmails: uniq(jobData.map((row) => row.last_activity_email || row.contact_email).filter(Boolean)),
      contactPhones: uniq(jobData.map((row) => row.contact_phone || row.phone || row.phone_number).filter(Boolean)),
      boards: uniq(jobData.map((row) => row.source || row.post_link).filter(Boolean)),
      notes: uniq(jobData.map((row) => row.notes || row.last_activity_detail).filter(Boolean)),
    }),
    [filterOptions, jobData]
  );

  const jobDetailFieldOptions = useMemo(() => {
    const baseOptions = buildDetailFieldOptions(jobData);
    return {
      locations: filterOptions.locations?.length ? filterOptions.locations : baseOptions.locations,
      industries: normalizeIndustrySelectionValues([
        ...(baseOptions.industries || []),
        ...(advancedSearchOptions.industries || []),
      ]),
      hardSkills: uniq([...(baseOptions.hardSkills || []), ...(filterOptions.tech_stacks || [])]),
      softSkills: baseOptions.softSkills || [],
      skills: uniq([...(baseOptions.skills || []), ...(filterOptions.tech_stacks || [])]),
    };
  }, [advancedSearchOptions.industries, filterOptions.locations, filterOptions.tech_stacks, jobData]);

  const getToken = () =>
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("jwt") ||
    localStorage.getItem("accessToken") ||
    "";

  const authFetch = useCallback(async (url, options = {}) => {
    const token = getToken();
    const headers = {
      ...(options.headers || {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(token ? { "x-access-token": token } : {}),
    };
    return fetch(url, { ...options, headers });
  }, []);

  useEffect(() => {
    let active = true;

    authFetch(`${API_BASE}/api/candidates?summary=pipeline-count`)
      .then(async (response) => {
        if (!response.ok) throw new Error("Failed to load recruiting pipeline count");
        return response.json();
      })
      .then((payload) => {
        if (!active) return;
        const total = Number(payload?.count);
        setRecruitingPipelineTotal(Number.isFinite(total) ? Math.max(0, total) : null);
      })
      .catch((error) => {
        if (active) {
          console.error("Failed to fetch recruiting pipeline count:", error);
          setRecruitingPipelineTotal(null);
        }
      });

    return () => {
      active = false;
    };
  }, [authFetch, reloadKey]);

  useEffect(() => {
    if (listTab !== "recruiting-pipeline") return undefined;

    let active = true;
    setRecruitingPipelineLoading(true);
    const query = new URLSearchParams({
      page: String(recruitingPipelinePage),
      limit: String(recruitingPipelineRowsPerPage),
      has_job: "1",
    });
    if (search.trim()) query.set("search", search.trim());
    if (recruitingPipelineStage) query.set("stats_filter", pipelineStageKey(recruitingPipelineStage));
    if (appliedFilters.activities?.length) query.set("job_activity", JSON.stringify(appliedFilters.activities));

    authFetch(`${API_BASE}/api/candidates?${query.toString()}`)
      .then(async (response) => {
        if (!response.ok) throw new Error("Failed to load recruiting pipeline");
        return response.json();
      })
      .then((payload) => {
        if (!active) return;
        const rows = Array.isArray(payload?.rows)
          ? payload.rows
          : Array.isArray(payload?.candidates)
            ? payload.candidates
            : Array.isArray(payload?.data)
              ? payload.data
              : [];
        // `has_job=1` is enforced by the API. Rendering every returned row is
        // important: optional job-summary hydration must not shrink a requested
        // 10-row page down to five on its first load.
        setRecruitingPipelineRows(rows);
        setRecruitingPipelineFilteredTotal(Number(payload.total || 0));
      })
      .catch((error) => {
        if (active) setLoadError(error?.message || "Failed to load recruiting pipeline");
      })
      .finally(() => {
        if (active) setRecruitingPipelineLoading(false);
      });

    return () => {
      active = false;
    };
  }, [authFetch, listTab, recruitingPipelinePage, recruitingPipelineRowsPerPage, search, recruitingPipelineStage, reloadKey, appliedFilters.activities]);

  useEffect(() => {
    let active = true;
    authFetch(`${API_BASE}/api/candidates?summary=stats&has_job=1`).then(response => {
      if (!response.ok) throw new Error("Unable to load pipeline counts");
      return response.json();
    }).then(data => { if (active) setRecruitingStageStats(data); }).catch(() => {});
    return () => { active = false; };
  }, [authFetch, reloadKey, recruitingPipelineRows]);

  const clearPendingJobEnrichmentRefreshTimeout = useCallback(() => {
    if (pendingJobEnrichmentRefreshTimeoutRef.current !== null) {
      window.clearTimeout(pendingJobEnrichmentRefreshTimeoutRef.current);
      pendingJobEnrichmentRefreshTimeoutRef.current = null;
    }
  }, []);

  const syncOpenJobStatesFromRows = useCallback((rows = []) => {
    const rowsById = new Map(
      (Array.isArray(rows) ? rows : [])
        .filter((row) => row?.id != null)
        .map((row) => [String(row.id), row])
    );

    if (!rowsById.size) return;

    const mergeOpenJob = (currentJob) => {
      if (!currentJob?.id) return currentJob;

      const nextRow = rowsById.get(String(currentJob.id));
      return nextRow
        ? sanitizeJobCompanyContactFields({ ...currentJob, ...nextRow })
        : currentJob;
    };

    setSelectedJob((prev) => mergeOpenJob(prev));
    setDetailJob((prev) => mergeOpenJob(prev));
  }, []);

  const mergeJobIntoVisibleState = useCallback((nextJob) => {
    const normalizedJob = withJobActivitySortFields(
      sanitizeJobCompanyContactFields(nextJob || {})
    );
    const normalizedJobId = String(normalizedJob?.id || "").trim();
    if (!normalizedJobId) return;

    const mergeOpenJob = (currentJob) =>
      currentJob && String(currentJob?.id) === normalizedJobId
        ? withJobActivitySortFields(
            sanitizeJobCompanyContactFields({ ...currentJob, ...normalizedJob })
          )
        : currentJob;

    setJobData((prev) =>
      (Array.isArray(prev) ? prev : []).map((row) =>
        String(row?.id) === normalizedJobId
          ? withJobActivitySortFields(
              sanitizeJobCompanyContactFields({ ...row, ...normalizedJob })
            )
          : row
      )
    );
    setSelectedJob((prev) => mergeOpenJob(prev));
    setDetailJob((prev) => mergeOpenJob(prev));
  }, []);

  useEffect(() => {
    let active = true;

    authFetch(`${API_BASE}/api/hiring-managers?summary=count`)
      .then(async (response) => {
        if (!response.ok) throw new Error("Failed to fetch contacts count");
        return response.json();
      })
      .then((countData) => {
        if (!active) return;
        const total = Number(countData?.count);
        setContactRowsTotal(Number.isFinite(total) ? Math.max(0, total) : null);
      })
      .catch((err) => {
        if (!active) return;
        console.error("Failed to fetch contacts count:", err);
        setContactRowsTotal(null);
      });

    return () => {
      active = false;
    };
  }, [authFetch, reloadKey]);

  useEffect(() => {
    let active = true;
    const query = buildContactListQuery({ page: contactTablePage, limit: contactTableRowsPerPage, ...contactFilters });
    const contactSearch = showingContacts ? search.trim() : "";
    if (contactSearch) query.set("search", contactSearch);

    authFetch(`${API_BASE}/api/hiring-managers?${query.toString()}`)
      .then(async (contactsResponse) => {
        if (!contactsResponse.ok) throw new Error("Failed to fetch contacts");
        const data = await contactsResponse.json();
        if (!active) return;
        const raw = Array.isArray(data) ? data : data.rows || [];
        setContactRows(
          raw.map((row) => ({
            id: row.id,
            name: row.name || "",
            role: normalizeContactRoleLabel(row.role || row.title, {
              contactKind: row.contact_kind,
              source: row.source,
              fallback: "Hiring Manager",
            }),
            status: row.status || "New",
            owner: row.owner || row.source || "-",
            source: row.source || "-",
            email: row.email || "",
            phone: row.phone || row.phone_number || "",
            linkedin_url: row.linkedin_url || "",
            profile_image: row.profile_image || "",
            entity_type: row.entity_type || "",
            entity_id: row.entity_id,
            entity_name: row.entity_name || "",
            entity_logo: row.entity_logo || "",
            entity_website: row.entity_website || row.company_website || "",
            entity_title: row.entity_title || "",
            contact_kind: row.contact_kind || "",
            created_at: row.created_at,
            updated_at: row.updated_at,
          }))
        );
        const total = Number(data?.total);
        setContactTableTotal(Number.isFinite(total) ? Math.max(0, total) : raw.length);
      })
      .catch((err) => {
        if (!active) return;
        console.error("Failed to fetch contacts:", err);
        setContactTableTotal(0);
      });

    return () => {
      active = false;
    };
  }, [authFetch, contactTablePage, contactTableRowsPerPage, reloadKey, search, showingContacts, contactFilters]);

  const enrichedContactRows = useMemo(() => {
    const jobMap = new Map(jobData.map((r) => [String(r.id), r]));
    const companyMap = new Map(
      jobData
        .filter((row) => Number.isFinite(Number(row?.company_id)) && Number(row.company_id) > 0)
        .map((row) => [String(Number(row.company_id)), row])
    );
    return contactRows.map((c) => {
      const entityType = normalizeHiringManagerEntityType(c.entity_type);
      const entityId = String(c.entity_id ?? "");

      const entity = entityType === "job" ? jobMap.get(entityId) : companyMap.get(entityId);
      return {
        ...c,
        entity_type: entityType || c.entity_type || "",
        entity_name: c.entity_name || entity?.company_name || "",
        entity_logo: c.entity_logo || entity?.company_logo || "",
        entity_title: entityType === "job" ? c.entity_title || entity?.job_title || "" : "",
      };
    });
  }, [contactRows, jobData]);

  const contactColumns = useMemo(
    () => [
      {
        id: "name",
        label: "Name",
        minWidth: 190,
        renderCell: (row) => (
          <Stack
            direction="row"
            spacing={1.25}
            alignItems="center"
            onClick={() => navigate(`/contacts/${encodeURIComponent(String(row.id))}`, { state: { contact: row, companyName: row.entity_name } })}
            sx={{ cursor: "pointer" }}
          >
            <Avatar
              src={row.profile_image || undefined}
              sx={{
                width: 30,
                height: 30,
                background: "radial-gradient(circle at 30% 30%, #a855f7, #6366f1)",
              }}
            >
              {row.name?.[0]}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" fontWeight={600} noWrap>
                {row.name}
              </Typography>
              <Typography
                variant="caption"
                sx={{ display: "block", mt: 0.35, width: 200, maxWidth: 200, color: "#98A2B3", lineHeight: 1.2 }}
                noWrap
              >
                {row.role || "Hiring Manager"}
              </Typography>
            </Box>
          </Stack>
        ),
      },
      {
        id: "entity_name",
        label: "Company Name",
        minWidth: 190,
        renderCell: (row) => <CompanyTableCell row={row} />,
      },
      {
        id: "status",
        label: "Status",
        minWidth: 118,
        renderCell: (row) => (
          <Chip
            label={row.status || "New"}
            size="small"
            sx={{ bgcolor: "#F2F4F7", color: "#475467", fontWeight: 600, fontSize: 12 }}
          />
        ),
      },
      { id: "role", label: "Role", minWidth: 160 },
      {
        id: "owner",
        label: "Source",
        minWidth: 130,
        renderCell: (row) => (
          <Typography variant="body2" sx={{ color: "#475467" }} noWrap>
            {row.owner || "-"}
          </Typography>
        ),
      },
      {
        id: "contact_information",
        label: "Contact information",
        minWidth: 120,
        renderCell: (row) => (
          <InfoActionIcons
            showDisabled
            showGroupDivider={false}
            preserveOrder
            gap={0.8}
            iconSize={18}
            actions={[
              { type: "linkedin", href: row.linkedin_url },
              { type: "email", href: row.email },
              { type: "phone", href: row.phone },
            ]}
          />
        ),
      },
      {
        id: "actions",
        label: "Actions",
        minWidth: 64,
        sortable: false,
        renderCell: (row) => (
          <IconButton
            size="small"
            aria-label={`Open ${row?.name || "contact"}`}
            onClick={() => navigate(`/contacts/${encodeURIComponent(String(row.id))}`, { state: { contact: row, companyName: row.entity_name } })}
            sx={{ color: "#98A2B3" }}
          >
            <MoreVertIcon sx={{ fontSize: 18, color: "currentColor" }} />
          </IconButton>
        ),
      },
    ],
    [navigate]
  );

  const renderContactRow = useCallback(
    (row) => (
      <>
        {contactColumns.map((col) => (
          <TableCell
            key={col.id}
            align={col.align || "left"}
            sx={{
              minWidth: col.minWidth,
              width: col.width,
              maxWidth: col.maxWidth,
            }}
          >
            {col.renderCell ? col.renderCell(row) : row[col.id]}
          </TableCell>
        ))}
      </>
    ),
    [contactColumns]
  );

  const filteredContactRows = useMemo(() => {
    let filtered = enrichedContactRows.filter(Boolean);

    if (activeStatId && activeStatId !== "total") {
      const contactStatMatcher = {
        hiringManager: (row) => isHiringManagerContactRow(row),
        jobContacts: (row) => normalizeHiringManagerEntityType(row?.entity_type) === "job",
        companyContacts: (row) => {
          const entityType = normalizeHiringManagerEntityType(row?.entity_type);
          return entityType === "company" || entityType === "client";
        },
        withSource: (row) => {
          const source = String(row?.source || "").trim();
          return Boolean(source) && source !== "-";
        },
      };
      const match = contactStatMatcher[activeStatId];
      if (match) filtered = filtered.filter((row) => match(row));
    }

    if (!search.trim()) return filtered;
    const q = search.trim().toLowerCase();
    return filtered.filter((r) =>
      [r.name, r.role, r.entity_type, r.source, r.entity_name, r.entity_title]
        .filter(Boolean)
        .some((v) => v.toString().toLowerCase().includes(q))
    );
  }, [activeStatId, enrichedContactRows, search]);

  const handleContactServerPageChange = useCallback((nextPage) => {
    setContactTablePage(Math.max(1, Number(nextPage) + 1));
  }, []);

  const handleContactServerRowsPerPageChange = useCallback((nextRowsPerPage) => {
    setContactTableRowsPerPage(Math.max(1, Number(nextRowsPerPage) || 10));
    setContactTablePage(1);
  }, []);

  const handleRecruitingPipelinePageChange = useCallback((nextPage) => {
    setRecruitingPipelinePage(Math.max(1, Number(nextPage) + 1));
  }, []);

  const handleRecruitingPipelineRowsPerPageChange = useCallback((nextRowsPerPage) => {
    setRecruitingPipelineRowsPerPage(Math.max(1, Number(nextRowsPerPage) || 10));
    setRecruitingPipelinePage(1);
  }, []);

  const recruitingPipelineColumns = useMemo(
    () => [
      {
        id: "candidate_name",
        label: "Candidate",
        minWidth: 210,
        renderCell: (row) => {
          const name = row?.full_name || row?.candidate_name || "Candidate";
          return (
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              onClick={() => navigate(`/candidates?candidateId=${encodeURIComponent(String(row?.id || row?.candidate_id || ""))}`)}
              sx={{ cursor: "pointer" }}
            >
              <Avatar src={row?.avatar_url || ""} sx={{ width: 30, height: 30 }}>
                {name.charAt(0).toUpperCase()}
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 600 }} noWrap>{name}</Typography>
                <Typography sx={{ fontSize: 12, color: "#667085" }} noWrap>{row?.title || row?.candidate_title || ""}</Typography>
              </Box>
            </Stack>
          );
        },
      },
      { id: "job_title", label: "Job Title", minWidth: 220 },
      {
        id: "job_company_name",
        label: "Company Name",
        minWidth: 180,
        renderCell: (row) => <PipelineCompanyTableCell row={row} />,
      },
      {
        id: "job_stage",
        label: "Current Stage",
        minWidth: 140,
        renderCell: (row) => (
          <Chip label={row?.job_stage || row?.stage || row?.candidate_stage || "-"} size="small" sx={{ color: stageColor(row?.job_stage || row?.stage || row?.candidate_stage), backgroundColor: "#fff", border: "1px solid currentColor" }} />
        ),
      },
      {
        id: "team_member",
        label: "Team Member",
        minWidth: 160,
        renderCell: (row) => row?.owner || row?.recruiter_name || row?.assigned_team_member || row?.team_name || "-",
      },
      { id: "location", label: "Location", minWidth: 150 },
    ],
    [navigate]
  );
  const renderRecruitingPipelineRow = useCallback(
    (row) => (
      <>{recruitingPipelineColumns.map((column) => (
        <TableCell key={column.id} sx={{ minWidth: column.minWidth, py: 1.2 }}>
          {column.renderCell ? column.renderCell(row) : row?.[column.id] || "-"}
        </TableCell>
      ))}</>
    ),
    [recruitingPipelineColumns]
  );
  const tableResetSignal = useMemo(
    () =>
      JSON.stringify({
        listTab,
        search,
        activeStatId,
        appliedFilters,
        advancedApplied,
        reloadKey,
      }),
    [activeStatId, advancedApplied, appliedFilters, listTab, reloadKey, search]
  );

  useEffect(() => {
    setActiveStatId("total");
  }, [listTab]);

  const clearPendingImportRefreshTimeout = useCallback(() => {
    if (pendingRefreshTimeoutRef.current !== null) {
      window.clearTimeout(pendingRefreshTimeoutRef.current);
      pendingRefreshTimeoutRef.current = null;
    }
  }, []);

  const stopPendingImportRefresh = useCallback(() => {
    clearPendingImportRefreshTimeout();
    pendingImportedJobIdsRef.current = [];
    pendingImportRefreshAttemptsRef.current = 0;
  }, [clearPendingImportRefreshTimeout]);

  const updateJobSearchParam = useCallback(
    (jobId, replace = false, tab = "", companyId = "") => {
      const params = new URLSearchParams(location.search);
      const nextJobId = jobId == null || jobId === "" ? "" : String(jobId);
      const nextTab = normalizeJobRouteTab(tab);
      const nextCompanyId = companyId == null || companyId === "" ? "" : String(companyId);

      if (nextJobId) {
        params.set("jobId", nextJobId);
        if (nextTab) params.set("tab", nextTab);
        else params.delete("tab");
        if (nextCompanyId) params.set("companyId", nextCompanyId);
        else params.delete("companyId");
      } else {
        params.delete("jobId");
        params.delete("tab");
        params.delete("companyId");
      }

      const nextSearch = params.toString();
      const currentSearch = location.search.startsWith("?")
        ? location.search.slice(1)
        : location.search;

      if (nextSearch === currentSearch) return;

      navigate(
        {
          pathname: location.pathname,
          search: nextSearch ? `?${nextSearch}` : "",
        },
        { replace }
      );
    },
    [location.pathname, location.search, navigate]
  );

  useEffect(() => {
    const requestId = jobsRequestIdRef.current + 1;
    jobsRequestIdRef.current = requestId;
    const controller = new AbortController();

    const fetchJobs = async () => {
      try {
        setJobsLoading(true);
        setLoadError("");

        const effectiveSearch = (appliedFilters.ai_agent || "").trim() ? appliedFilters.ai_agent.trim() : search;

        const query = toQuery({
          page,
          limit: rowsPerPage,
          search: effectiveSearch,
          job_title: appliedFilters.job_titles,
          tech_stack: appliedFilters.tech_stacks,
          location: appliedFilters.locations,
          owner: appliedFilters.owners,
          status: appliedFilters.statuses,
          activity: appliedFilters.activities,
          source: appliedFilters.sources,
          candidate_stage:
            JOB_PIPELINE_STAGE_FILTER_VALUES_BY_STAT_ID[activeStatId] || [],
          ...buildAdvancedJobQuery(advancedApplied),
        });

        const res = await authFetch(`${API_BASE}/api/jobs${query}`, {
          signal: controller.signal,
        });
        if (!res.ok) {
          const text = await res.text();
          throw new Error(`Failed to fetch jobs (status ${res.status}). ${text.slice(0, 180)}`);
        }

        const data = await res.json().catch(() => ({}));
        if (controller.signal.aborted || requestId !== jobsRequestIdRef.current) return;

        const rows = Array.isArray(data.rows)
          ? data.rows.map((row) =>
              withJobActivitySortFields(sanitizeJobCompanyContactFields(row))
            )
          : [];
        setJobData(rows);
        setJobsTotal(
          Number.isFinite(Number(data.total))
            ? Math.max(0, Number(data.total))
            : rows.length
        );
        if (Number.isFinite(Number(data.totalJobs))) {
          setAllJobsTotal(Math.max(0, Number(data.totalJobs)));
        }
        syncOpenJobStatesFromRows(rows);

        setSelectedIdsLocal((prev) => {
          if (!prev.length) return prev;
          const visible = new Set(rows.map((r) => String(r.id)));
          return prev.filter((id) => visible.has(String(id)));
        });
      } catch (err) {
        if (controller.signal.aborted || requestId !== jobsRequestIdRef.current) return;
        console.error("Failed to fetch jobs:", err);
        setJobData([]);
        setJobsTotal(0);
        setAllJobsTotal(0);
        setLoadError(err?.message || "Failed to fetch jobs.");
      } finally {
        if (requestId === jobsRequestIdRef.current) {
          setJobsLoading(false);
        }
      }
    };

    fetchJobs();
    return () => controller.abort();
  }, [
    page,
    rowsPerPage,
    search,
    appliedFilters,
    advancedApplied,
    activeStatId,
    authFetch,
    reloadKey,
    syncOpenJobStatesFromRows,
  ]);

  useEffect(() => {
    let active = true;

    const fetchAllJobsTotal = async () => {
      try {
        const res = await authFetch(`${API_BASE}/api/jobs?page=1&limit=1`);
        if (!res.ok) return;

        const data = await res.json().catch(() => ({}));
        const total = Number(data?.total);
        if (active && Number.isFinite(total)) {
          setAllJobsTotal(Math.max(0, total));
        }
      } catch (err) {
        console.error("Failed to fetch total jobs count:", err);
      }
    };

    fetchAllJobsTotal();
    return () => {
      active = false;
    };
  }, [authFetch, reloadKey]);

  useEffect(() => {
    const fetchPipelineStats = async () => {
      try {
        const responses = await Promise.all([
          authFetch(`${API_BASE}/api/jobs/stats/pipeline`),
          // Use the exact query used when the Assigned Recruiters card is
          // selected. This keeps its total and its filtered table results in
          // lockstep even if the summary endpoint is served from stale data.
          authFetch(`${API_BASE}/api/jobs?page=1&limit=1&status=Open&advanced_has_recruiter=1`),
        ]);
        const [statsResponse, assignedJobsResponse] = responses;

        const data = statsResponse.ok
          ? await statsResponse.json().catch(() => ({}))
          : {};
        const assignedJobsData = assignedJobsResponse.ok
          ? await assignedJobsResponse.json().catch(() => ({}))
          : {};
        const assignedRecruiters = Number(assignedJobsData?.total);

        setPipelineStats((prev) => ({
          ...prev,
          ...data,
          ...(Number.isFinite(assignedRecruiters)
            ? { assigned_recruiters: Math.max(0, assignedRecruiters) }
            : {}),
        }));
      } catch (err) {
        console.error("Failed to fetch job pipeline stats:", err);
      }
    };

    fetchPipelineStats();
  }, [authFetch, reloadKey]);

  useEffect(() => {
    const handleJobsUpdated = (event) => {
      const importedJobIds = Array.isArray(event?.detail?.importedJobIds)
        ? event.detail.importedJobIds
            .map((value) => Number(value))
            .filter((value) => Number.isFinite(value) && value > 0)
        : [];

      clearPendingImportRefreshTimeout();
      pendingImportedJobIdsRef.current = importedJobIds;
      pendingImportRefreshAttemptsRef.current = importedJobIds.length ? 6 : 4;
      setPage(1);
      setReloadKey((prev) => prev + 1);
    };

    window.addEventListener(JOBS_UPDATED_EVENT, handleJobsUpdated);
    return () => {
      stopPendingImportRefresh();
      window.removeEventListener(JOBS_UPDATED_EVENT, handleJobsUpdated);
    };
  }, [clearPendingImportRefreshTimeout, stopPendingImportRefresh]);

  useEffect(() => {
    if (jobsLoading) return undefined;

    const pendingImportedJobIds = pendingImportedJobIdsRef.current;
    const attemptsRemaining = Number(pendingImportRefreshAttemptsRef.current || 0);

    if (attemptsRemaining <= 0) {
      if (pendingImportedJobIds.length) {
        stopPendingImportRefresh();
      }
      return undefined;
    }

    const visibleIds = new Set((jobData || []).map((row) => String(row?.id)));
    const hasImportedJobVisible = pendingImportedJobIds.some((id) => visibleIds.has(String(id)));

    if (page === 1 && (!pendingImportedJobIds.length || hasImportedJobVisible)) {
      stopPendingImportRefresh();
      return undefined;
    }

    clearPendingImportRefreshTimeout();
    pendingRefreshTimeoutRef.current = window.setTimeout(() => {
      pendingRefreshTimeoutRef.current = null;
      pendingImportRefreshAttemptsRef.current = Math.max(
        0,
        Number(pendingImportRefreshAttemptsRef.current || 0) - 1
      );
      setReloadKey((prev) => prev + 1);
    }, 1200);

    return () => {
      clearPendingImportRefreshTimeout();
    };
  }, [jobData, jobsLoading, page, clearPendingImportRefreshTimeout, stopPendingImportRefresh]);

  useEffect(() => {
    clearPendingJobEnrichmentRefreshTimeout();

    if (showingContacts || jobsLoading) {
      return undefined;
    }

    const hasVisiblePendingJobEnrichment = filteredJobRows.some((row) =>
      Boolean(row?.job_details_enrichment_pending)
    );
    if (!hasVisiblePendingJobEnrichment) {
      return undefined;
    }

    pendingJobEnrichmentRefreshTimeoutRef.current = window.setTimeout(() => {
      pendingJobEnrichmentRefreshTimeoutRef.current = null;
      setReloadKey((prev) => prev + 1);
    }, 2500);

    return () => {
      clearPendingJobEnrichmentRefreshTimeout();
    };
  }, [
    clearPendingJobEnrichmentRefreshTimeout,
    filteredJobRows,
    jobsLoading,
    showingContacts,
  ]);

  useEffect(() => {
    if (!quickOpen || !selectedJob?.id) return;
    if (hasLoadedJobDetailFields(selectedJob) && !selectedJob?.job_details_enrichment_pending) {
      return;
    }

    let active = true;
    const selectedJobId = selectedJob.id;

    const fetchJobDetails = async () => {
      try {
        const res = await authFetch(`${API_BASE}/api/jobs/${encodeURIComponent(selectedJobId)}`);
        if (!res.ok) {
          const text = await res.text().catch(() => "");
          throw new Error(
            `Failed to fetch job details (${res.status}). ${text.slice(0, 180)}`
          );
        }

        const data = await res.json().catch(() => ({}));
        const nextJob = sanitizeJobCompanyContactFields(data?.job || data || {});
        if (!active || !nextJob) return;

        mergeJobIntoVisibleState(nextJob);
      } catch (err) {
        console.error("Failed to fetch quick job details:", err);
      }
    };

    fetchJobDetails();

    return () => {
      active = false;
    };
  }, [quickOpen, selectedJob, selectedJob?.id, authFetch, mergeJobIntoVisibleState]);

  useEffect(() => {
    if (sidebarResetToken) return;

    const params = new URLSearchParams(location.search);
    const jobId = params.get("jobId");
    const routeTab = normalizeJobRouteTab(params.get("tab"));
    const openInDetails = Boolean(routeTab);
    if (!jobId) return;

    if (
      openInDetails &&
      detailJob &&
      String(detailJob.id) === String(jobId)
    ) {
      setQuickOpen(false);
      return;
    }

    if (!openInDetails && quickOpen && selectedJob && String(selectedJob.id) === String(jobId)) {
      return;
    }

    const localHit = jobData.find((row) => String(row?.id) === String(jobId));
    if (localHit) {
      const resolvedLocalHit =
        selectedJob && String(selectedJob?.id) === String(jobId)
          ? sanitizeJobCompanyContactFields({ ...localHit, ...selectedJob })
          : sanitizeJobCompanyContactFields(localHit);

      if (openInDetails) {
        setQuickOpen(false);
        setDetailJob(resolvedLocalHit);
        onDetailOpen?.(resolvedLocalHit);
      } else {
        setSelectedJob(resolvedLocalHit);
        setQuickOpen(true);
      }
      return;
    }

    let active = true;

    const fetchJobById = async () => {
      try {
        const res = await authFetch(`${API_BASE}/api/jobs/${encodeURIComponent(jobId)}`);
        if (!res.ok) {
          const text = await res.text().catch(() => "");
          throw new Error(`Failed to fetch deep-linked job (${res.status}). ${text.slice(0, 180)}`);
        }

        const data = await res.json().catch(() => ({}));
        const nextJob = sanitizeJobCompanyContactFields(data?.job || data || {});
        if (!active || !nextJob) return;

        mergeJobIntoVisibleState(nextJob);

        if (openInDetails) {
          setQuickOpen(false);
          setDetailJob(nextJob);
          onDetailOpen?.(nextJob);
        } else {
          setSelectedJob(nextJob);
          setQuickOpen(true);
        }
      } catch (err) {
        console.error("Failed to open job from URL:", err);
      }
    };

    fetchJobById();

    return () => {
      active = false;
    };
  }, [
    location.search,
    jobData,
    selectedJob,
    quickOpen,
    detailJob,
    authFetch,
    mergeJobIntoVisibleState,
    onDetailOpen,
    sidebarResetToken,
  ]);

  const handleOpenJob = useCallback((row) => {
    const nextJob = sanitizeJobCompanyContactFields(row);
    setSelectedJob((prev) =>
      prev && String(prev?.id) === String(nextJob?.id)
        ? sanitizeJobCompanyContactFields({ ...prev, ...nextJob })
        : nextJob
    );
    setQuickOpen(true);
  }, []);

  const handleOpenRowMenu = useCallback((event, row) => {
    event.stopPropagation();
    setRowMenuAnchorEl(event.currentTarget);
    setRowMenuItem(row || null);
  }, []);

  const handleCloseRowMenu = useCallback(() => {
    setRowMenuAnchorEl(null);
    setRowMenuItem(null);
  }, []);

  const handleCloseQuickOpen = useCallback(() => {
    setQuickOpen(false);
    const params = new URLSearchParams(location.search);
    const hasDeepLinkedQuickDrawer =
      Boolean(params.get("jobId")) && !normalizeJobRouteTab(params.get("tab"));
    if (hasDeepLinkedQuickDrawer) {
      updateJobSearchParam("", true);
    }
  }, [location.search, updateJobSearchParam]);

  const openFilter = () => {
    setDraftFilters(appliedFilters);
    setIsFilterOpen(true);
  };

  const clearFilters = () => {
    setRecruitingPipelinePage(1);
    setDraftFilters(emptyFilters);
    setAppliedFilters(emptyFilters);
    setAdvancedApplied(null);
    setPage(1);
  };

  const applyFilters = () => {
    setRecruitingPipelinePage(1);
    setPage(1);
    setAppliedFilters(draftFilters);
    setIsFilterOpen(false);
  };

  const updateAppliedFiltersFromResults = useCallback((updater) => {
    setAppliedFilters((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      setDraftFilters(next);
      setAdvancedApplied((current) =>
        current ? mergeBasicIntoAdvancedJobFilters(next, current) : current
      );
      return next;
    });
    setPage(1);
  }, []);

  const handleRemoveFilterPill = useCallback(
    (field, value) => {
      updateAppliedFiltersFromResults((prev) => {
        if (field === "ai_agent") {
          return { ...prev, ai_agent: "" };
        }

        return {
          ...prev,
          [field]: removeFilterValueFromList(prev[field], value),
        };
      });
    },
    [updateAppliedFiltersFromResults]
  );

  const resultsFilterPills = useMemo(() => {
    const pills = [];

    const appendTextPill = (field, value, prefix = "") => {
      const label = cleanText(value);
      if (!label) return;

      pills.push({
        key: `${field}:${normalizeFilterChipValue(label)}`,
        label: `${prefix}${label}`,
        onDelete: () => handleRemoveFilterPill(field, label),
      });
    };

    const appendArrayPills = (field, values = []) => {
      (values || []).forEach((value) => {
        const label = cleanText(value);
        if (!label) return;

        pills.push({
          key: `${field}:${normalizeFilterChipValue(label)}`,
          label,
          onDelete: () => handleRemoveFilterPill(field, label),
        });
      });
    };

    appendTextPill("ai_agent", appliedFilters.ai_agent, "AI: ");
    appendArrayPills("job_titles", appliedFilters.job_titles);
    appendArrayPills("tech_stacks", appliedFilters.tech_stacks);
    appendArrayPills("locations", appliedFilters.locations);
    appendArrayPills("owners", appliedFilters.owners);
    appendArrayPills("statuses", appliedFilters.statuses);
    appendArrayPills("activities", appliedFilters.activities);
    appendArrayPills("sources", appliedFilters.sources);

    return pills;
  }, [appliedFilters, handleRemoveFilterPill]);

  const openAdvancedSearch = useCallback(
    (drawerFilters) => {
      const nextBasicFilters = drawerFilters || draftFilters;
      const normalizedBasicFilters = {
        ...emptyFilters,
        ...nextBasicFilters,
        job_titles: uniq(nextBasicFilters?.job_titles || []),
        tech_stacks: uniq(nextBasicFilters?.tech_stacks || []),
        locations: normalizeJobLocationList(nextBasicFilters?.locations || []),
        owners: uniq(nextBasicFilters?.owners || []),
        statuses: uniq(nextBasicFilters?.statuses || []),
        activities: uniq(nextBasicFilters?.activities || []),
        sources: uniq(nextBasicFilters?.sources || []),
      };

      setDraftFilters(normalizedBasicFilters);
      setAppliedFilters(normalizedBasicFilters);
      setAdvancedApplied((prev) => mergeBasicIntoAdvancedJobFilters(normalizedBasicFilters, prev));
      setIsFilterOpen(false);
      setPage(1);
      setShowAdvancedSearch(true);
    },
    [draftFilters, emptyFilters]
  );

  const closeAdvancedSearch = useCallback(() => {
    setShowAdvancedSearch(false);
  }, []);

  const applyAdvancedSearch = useCallback(
    (advancedPayload) => {
      const normalizedAdvanced = normalizeJobAdvancedFilters(advancedPayload);
      const nextBasicFilters = advancedJobFiltersToBasicFilters(normalizedAdvanced, emptyFilters);

      setAdvancedApplied(normalizedAdvanced);
      setDraftFilters(nextBasicFilters);
      setAppliedFilters(nextBasicFilters);
      setPage(1);
      setShowAdvancedSearch(false);
    },
    [emptyFilters]
  );

  const openDetails = useCallback(
    async (job, targetTab = "details") => {
      const jobId = job?.id;
      const baseJob =
        selectedJob && String(selectedJob?.id) === String(jobId)
          ? sanitizeJobCompanyContactFields({ ...(job || {}), ...selectedJob })
          : sanitizeJobCompanyContactFields(job || {});

      setQuickOpen(false);
      setSelectedJob(baseJob || null);
      setDetailJob(baseJob || null);
      if (jobId != null && jobId !== "") {
        updateJobSearchParam(
          jobId,
          false,
          normalizeJobRouteTab(targetTab) || "details",
          job?.company_id || job?.companyId || baseJob?.company_id || baseJob?.companyId || ""
        );
      }
      onDetailOpen?.(baseJob || sanitizeJobCompanyContactFields(job || {}));

      const shouldFetchFullJob =
        !hasLoadedJobDetailFields(baseJob || {}) ||
        Boolean(baseJob?.job_details_enrichment_pending);

      if (jobId == null || jobId === "" || !shouldFetchFullJob) return;

      try {
        const res = await authFetch(`${API_BASE}/api/jobs/${encodeURIComponent(jobId)}`);
        if (!res.ok) {
          const text = await res.text().catch(() => "");
          throw new Error(`Failed to fetch full job details (${res.status}). ${text.slice(0, 180)}`);
        }

        const data = await res.json().catch(() => ({}));
        const nextJob = sanitizeJobCompanyContactFields(data?.job || data || {});
        if (!nextJob || typeof nextJob !== "object") return;

        mergeJobIntoVisibleState(nextJob);
      } catch (err) {
        console.error("Failed to prefetch full job details for Show more:", err);
      }
    },
    [authFetch, mergeJobIntoVisibleState, onDetailOpen, selectedJob, updateJobSearchParam]
  );

  const closeDetails = useCallback(() => {
    setQuickOpen(false);
    setSelectedJob(null);
    setDetailJob(null);
    setIsFilterOpen(false);
    setRowMenuAnchorEl(null);
    setRowMenuItem(null);
    navigate(
      {
        pathname: location.pathname,
        search: "",
      },
      {
        replace: true,
        state: createSidebarResetState(location.pathname),
      }
    );
    onDetailClose?.();
  }, [location.pathname, navigate, onDetailClose]);

  const isAdminUser = ADMIN_ROLES.has(cleanText(user?.role).toLowerCase());

  const columns = useMemo(
    () => [
      {
        id: "job_title",
        label: "Job Title",
        minWidth: 200,
        width: 200,
        maxWidth: 200,
        renderCell: (row) => {
          const jobId = cleanText(row?.job_id || row?.id);
          return (
            <Box sx={{ minWidth: 0 }}>
              <Typography
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  handleOpenJob(row);
                }}
                sx={{
                  color: "#344054",
                  fontFamily: "Be Vietnam Pro",
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
                noWrap
              >
                {row.job_title || "-"}
              </Typography>
              <Typography sx={TABLE_META_TEXT_SX} noWrap>
                {jobId ? `Job ID: ${jobId}` : "-"}
              </Typography>
              <InlineBackgroundTaskLabel entity={row} />
            </Box>
          );
        },
      },
      {
        id: "company_name",
        label: "Company Name",
        minWidth: 180,
        width: 180,
        maxWidth: 180,
        renderCell: (row) => <CompanyTableCell row={row}>{isActiveClientCompanyStatus(row?.linked_company_status || row?.company_status || row?.client_status || "") ? <Chip label="Active Client" size="small" sx={ACTIVE_CLIENT_BADGE_SX} /> : null}</CompanyTableCell>,
      },
      {
        id: "status",
        label: "Status",
        minWidth: 78,
        width: 78,
        maxWidth: 78,
        renderCell: (row) => {
          const statusLabel = normalizeJobStatusDisplay(row.status);
          const style = STATUS_STYLE[statusLabel] || STATUS_STYLE[row.status] || { bg: "#eee", color: "#444" };
          return (
            <Chip
              label={statusLabel || row.status}
              size="small"
              sx={{
                height: 24,
                borderRadius: "999px",
                backgroundColor: style.bg,
                color: style.color,
                fontWeight: 700,
                fontFamily: "Be Vietnam Pro",
                fontSize: 11,
              }}
            />
          );
        },
      },
      {
        id: "priority",
        label: "Priority",
        minWidth: 78,
        width: 78,
        maxWidth: 78,
        renderCell: (row) => {
          const priorityLabel = normalizeJobPriorityLabel(
            row?.priority || row?.job_priority || row?.priority_level || row?.urgency
          );
          const style = JOB_PRIORITY_STYLE[priorityLabel];
          if (!priorityLabel || !style) {
            return (
              <Typography sx={{ fontSize: 14, color: "#98A2B3", fontFamily: "Be Vietnam Pro" }}>
                -
              </Typography>
            );
          }
          return (
            <Chip
              label={priorityLabel}
              size="small"
              sx={{
                height: 24,
                borderRadius: "999px",
                backgroundColor: style.bg,
                color: style.color,
                fontWeight: 700,
                fontFamily: "Be Vietnam Pro",
                fontSize: 11,
              }}
            />
          );
        },
      },
      {
        id: "owner",
        label: "Owner",
        minWidth: 148,
        width: 148,
        maxWidth: 148,
        sortable: false,
        renderCell: (row) => {
          const ownerName = getSavedJobOwnerDisplay(row);
          const ownerRole = cleanText(
            row?.owner_role || row?.owner_title || row?.recruiter_role || "Owner"
          );
          return (
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: "#344054",
                  fontFamily: "Be Vietnam Pro",
                }}
                noWrap
              >
                {ownerName || "-"}
              </Typography>
              <Typography sx={TABLE_META_TEXT_SX} noWrap>
                {ownerRole || "-"}
              </Typography>
            </Box>
          );
        },
      },
      {
        id: "location",
        label: "Location",
        minWidth: 118,
        width: 118,
        maxWidth: 118,
        sortable: false,
        renderCell: (row) => {
          const locationLabel = cleanText(
            row?.location ||
              (Array.isArray(row?.locations_json) ? row.locations_json[0] : "") ||
              (Array.isArray(row?.locations) ? row.locations[0] : "")
          );
          const workArrangement = normalizeWorkArrangementLabel(row?.work_arrangement);

          return (
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 500,
                  color: "#344054",
                  fontFamily: "Be Vietnam Pro",
                }}
                noWrap
              >
                {locationLabel || "-"}
              </Typography>
              {workArrangement ? (
                <Chip
                  label={workArrangement}
                  size="small"
                  sx={{
                    mt: 0.4,
                    height: 20,
                    borderRadius: "999px",
                    backgroundColor: WORK_ARRANGEMENT_STYLE[workArrangement]?.bg || "#F2F4F7",
                    color: WORK_ARRANGEMENT_STYLE[workArrangement]?.color || "#667085",
                    fontSize: 10.5,
                    fontWeight: 700,
                    fontFamily: "Be Vietnam Pro",
                    "& .MuiChip-label": {
                      px: 1,
                    },
                  }}
                />
              ) : (
                <Typography sx={TABLE_META_TEXT_SX} noWrap>
                  -
                </Typography>
              )}
            </Box>
          );
        },
      },
      {
        id: "pipeline_total_count",
        label: "Pipeline",
        minWidth: 150,
        width: 150,
        maxWidth: 150,
        sortable: false,
        renderCell: (row) => {
          const pipeline = getJobPipelineSummary(row);
          const secondaryParts = [
            `Applied: ${pipeline.applied}`,
            `Matched: ${pipeline.matched}`,
            `Screened: ${pipeline.screened}`,
            `Presented: ${pipeline.presented}`,
            `Interview: ${pipeline.interview}`,
            `Offer: ${pipeline.offer}`,
            `Hired: ${pipeline.hired}`,
          ];

          return (
            <Box
              title="Open this job's candidate pipeline"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                openDetails(row, "stages");
              }}
              sx={{
                minWidth: 0,
                cursor: "pointer",
                "&:hover .job-pipeline-total": {
                  color: "#7C3AED",
                },
              }}
            >
              <Typography
                className="job-pipeline-total"
                sx={{
                  fontSize: 14,
                  fontWeight: 700,
                  color: "#344054",
                  fontFamily: "Be Vietnam Pro",
                }}
                noWrap
              >
                {pipeline.total} Total
              </Typography>
              <Typography
                sx={{
                  ...TABLE_META_TEXT_SX,
                  whiteSpace: "normal",
                }}
              >
                {secondaryParts.join(" • ")}
              </Typography>
            </Box>
          );
        },
      },
      {
        id: "assigned_team_member",
        label: "Assigned Team",
        minWidth: 74,
        width: 74,
        maxWidth: 74,
        sortable: false,
        renderCell: (row) => (
          <JobAvatarStackCell people={buildJobAssignedTeamPeople(row)} emptyLabel="-" />
        ),
      },
      {
        id: "job_list_last_activity_time",
        label: "Last Activity",
        minWidth: 88,
        width: 88,
        maxWidth: 88,
        renderCell: (row) => {
          return (
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: 14,
                  color: "#344054",
                  fontFamily: "Be Vietnam Pro",
                }}
                noWrap
              >
                {formatJobRelativeDate(getJobLastActivityValue(row))}
              </Typography>
              <Typography sx={TABLE_META_TEXT_SX} noWrap>
                {cleanText(row?.last_activity_type || row?.last_activity_subject) || "-"}
              </Typography>
            </Box>
          );
        },
      },
      {
        id: "created_at",
        label: "Date Opened",
        minWidth: 88,
        width: 88,
        maxWidth: 88,
        renderCell: (row) => (
          <Typography
            sx={{
              fontSize: 14,
              color: "#344054",
              fontFamily: "Be Vietnam Pro",
            }}
            noWrap
          >
            {formatJobOpenedDate(row.created_at)}
          </Typography>
        ),
      },
      {
        id: "client_portal_visibility",
        label: "Client Portal",
        minWidth: 80,
        width: 80,
        maxWidth: 80,
        sortable: false,
        renderCell: (row) => {
          const visibilityLabel = normalizeClientPortalVisibility(row);
          const style = CLIENT_PORTAL_STYLE[visibilityLabel];
          if (!visibilityLabel || !style) {
            return (
              <Typography sx={{ fontSize: 14, color: "#98A2B3", fontFamily: "Be Vietnam Pro" }}>
                -
              </Typography>
            );
          }

          return (
            <Chip
              label={visibilityLabel}
              size="small"
              sx={{
                height: 24,
                borderRadius: "999px",
                backgroundColor: style.bg,
                color: style.color,
                fontWeight: 700,
                fontFamily: "Be Vietnam Pro",
                fontSize: 11,
              }}
            />
          );
        },
      },
      {
        id: "actions",
        label: "Actions",
        minWidth: 48,
        width: 48,
        maxWidth: 48,
        sortable: false,
        renderCell: (row) => (
          <IconButton
            size="small"
            sx={{ color: "#98A2B3" }}
            onClick={(event) => handleOpenRowMenu(event, row)}
          >
            <MoreVertIcon sx={{ fontSize: 18, color: "currentColor" }} />
          </IconButton>
        ),
      },
    ],
    [handleOpenJob, handleOpenRowMenu, openDetails]
  );

  const renderRow = useCallback(
    (row) => (
      <>
        {columns.map((col) => (
          <TableCell
            key={col.id}
            sx={{
              minWidth: col.minWidth,
              width: col.width,
              maxWidth: col.maxWidth,
            }}
          >
            {col.renderCell ? col.renderCell(row) : row[col.id]}
          </TableCell>
        ))}
      </>
    ),
    [columns]
  );

  const handleOpenAddToSequence = useCallback(() => {
    setIsAddToSequenceOpen(true);
  }, []);

  const handleAssignTeamMember = useCallback(
    async (member, { role } = {}) => {
      const ids = selectedJobRows
        .map((row) => Number(row?.id))
        .filter((value) => Number.isFinite(value) && value > 0);

      if (!ids.length) return;

      const res = await authFetch(`${API_BASE}/api/jobs/bulk-assign-team-member`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ids,
          assignee_user_id: member?.id ?? null,
          assignee_name: member?.name || "",
          owner_role: role || "",
        }),
      });

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        const errorMessage = `Failed to assign team member (${res.status}). ${text.slice(0, 180)}`;
        setLoadError(errorMessage);
        throw new Error(errorMessage);
      }

      const idSet = new Set(ids.map((value) => String(value)));
      const nextAvatar = member?.avatar_url || "";

      const applyRowAssignment = (row) => {
        if (!row || !idSet.has(String(row?.id))) return row;

        return {
          ...row,
          owner: member?.name || row.owner,
          assigned_team_member: member?.name || row.assigned_team_member,
          recruiter_name: member?.name || row.recruiter_name,
          user_id: member?.id ?? row.user_id,
          recruiter_profile_image: nextAvatar || row.recruiter_profile_image || "",
          owner_profile_image: nextAvatar || row.owner_profile_image || "",
          profile_image: nextAvatar || row.profile_image || "",
        };
      };

      const applyDetailAssignment = (row) => {
        const next = applyRowAssignment(row);
        if (!next || !Array.isArray(next.contacts)) return next;

        return {
          ...next,
          contacts: next.contacts.map((item) =>
            /owner/i.test(cleanText(item?.role))
              ? {
                  ...item,
                  name: member?.name || item?.name || item?.owner || item?.owner_name || "",
                  avatar_url: nextAvatar || item?.avatar_url || item?.avatar || "",
                }
              : item
          ),
        };
      };

      setJobData((prev) => prev.map((row) => applyRowAssignment(row)));
      setSelectedJob((prev) => applyDetailAssignment(prev));
      setDetailJob((prev) => applyDetailAssignment(prev));
      setLoadError("");
    },
    [authFetch, selectedJobRows]
  );

  const updateJobStatusByIds = useCallback(
    async (ids, status) => {
      const normalizedIds = (Array.isArray(ids) ? ids : [])
        .map((value) => Number(value))
        .filter((value) => Number.isFinite(value) && value > 0);
      const nextStatus = normalizeJobStatusRequestValue(status);

      if (!normalizedIds.length || !nextStatus) {
        return "";
      }

      const res = await authFetch(`${API_BASE}/api/jobs/bulk-status`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ids: normalizedIds, status: nextStatus }),
      });

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(`Failed to update job status (${res.status}). ${text.slice(0, 180)}`);
      }

      const payload = await res.json().catch(() => ({}));
      const appliedStatus = cleanText(payload?.status) || nextStatus;
      const idSet = new Set(normalizedIds.map((value) => String(value)));

      const applyRowStatus = (row) => {
        if (!row || !idSet.has(String(row?.id))) return row;
        return { ...row, status: appliedStatus };
      };

      setJobData((prev) => prev.map((row) => applyRowStatus(row)));
      setSelectedJob((prev) => applyRowStatus(prev));
      setDetailJob((prev) => applyRowStatus(prev));
      setSelectedIds([]);
      setSelectedIdsLocal([]);
      // Re-fetch the active result set so a job that no longer matches a
      // status filter is removed immediately and the Results count is current.
      setReloadKey((prev) => prev + 1);
      return appliedStatus;
    },
    [authFetch]
  );

  const handleBulkStatusChange = useCallback(
    async (status) => {
      const ids = selectedJobRows
        .map((row) => Number(row?.id))
        .filter((value) => Number.isFinite(value) && value > 0);

      if (!ids.length) return;

      setLoadError("");
      try {
        const appliedStatus = await updateJobStatusByIds(ids, status);
        setStatusToast({
          open: true,
          severity: "success",
          message: `Updated ${ids.length} job${ids.length === 1 ? "" : "s"} to "${normalizeJobStatusDisplay(appliedStatus)}".`,
        });
      } catch (error) {
        console.error("Failed to update job statuses:", error);
        setLoadError(error?.message || "Failed to update job status.");
        setStatusToast({
          open: true,
          severity: "error",
          message: error?.message || "Failed to update job status.",
        });
      }
    },
    [selectedJobRows, updateJobStatusByIds]
  );

  const closeStatusToast = useCallback(() => {
    setStatusToast((prev) => ({ ...prev, open: false }));
  }, []);

  const showBulkMenuFeedback = useCallback((message, severity = "success") => {
    setStatusToast({
      open: true,
      severity,
      message: String(message || "").trim(),
    });
  }, []);

  const handleExportSelectedJobs = useCallback(() => {
    const exportRows = selectedJobRows.length ? selectedJobRows : filteredJobRows;
    if (!exportRows.length) return;

    const header = [
      "Job Title",
      "Job ID",
      "Company",
      "Status",
      "Priority",
      "Hiring Manager",
      "Hiring Manager Title",
      "Location",
      "Work Arrangement",
      "Pipeline Total",
      "Applied",
      "Matched",
      "Screened",
      "Presented",
      "Interview",
      "Offer",
      "Hired",
      "Assigned Team",
      "Last Activity",
      "Date Opened",
      "Client Portal",
    ];
    const lines = exportRows.map((row) => {
      const hiringManager = getJobHiringManagerDisplay(row);
      const pipeline = getJobPipelineSummary(row);
      const assignedTeam = buildJobAssignedTeamPeople(row)
        .map((person) => cleanText(person?.name))
        .filter(Boolean)
        .join(" | ");
      const workArrangement = normalizeWorkArrangementLabel(row?.work_arrangement);
      const locationLabel = cleanText(
        row?.location ||
          (Array.isArray(row?.locations_json) ? row.locations_json[0] : "") ||
          (Array.isArray(row?.locations) ? row.locations[0] : "")
      );

      return [
        row?.job_title || "-",
        cleanText(row?.job_id || row?.id) || "-",
        cleanText(row?.company_name || row?.linked_company_name) || "-",
        normalizeJobStatusDisplay(row?.status) || "-",
        normalizeJobPriorityLabel(
          row?.priority || row?.job_priority || row?.priority_level || row?.urgency
        ) || "-",
        hiringManager.name || "-",
        hiringManager.title || "-",
        locationLabel || "-",
        workArrangement || "-",
        pipeline.total,
        pipeline.applied,
        pipeline.matched,
        pipeline.screened,
        pipeline.presented,
        pipeline.interview,
        pipeline.offer,
        pipeline.hired,
        assignedTeam || "-",
        formatJobRelativeDate(getJobLastActivityValue(row)),
        formatJobOpenedDate(row?.created_at),
        normalizeClientPortalVisibility(row) || "-",
      ]
        .map(toCsvValue)
        .join(",");
    });

    const blob = new Blob([[header.map(toCsvValue).join(","), ...lines].join("\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const downloadUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = downloadUrl;
    link.download = `jobs-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(downloadUrl), 0);

    showBulkMenuFeedback(
      `Exported ${exportRows.length} job${exportRows.length === 1 ? "" : "s"} to CSV.`
    );
  }, [filteredJobRows, selectedJobRows, showBulkMenuFeedback]);

  const handleBulkExport = useCallback(
    (option) => {
      if (cleanText(option).toLowerCase() !== "csv_export") return;
      handleExportSelectedJobs();
    },
    [handleExportSelectedJobs]
  );

  const handleBulkSoftArchive = useCallback(async () => {
    const ids = selectedJobRows
      .map((row) => Number(row?.id))
      .filter((value) => Number.isFinite(value) && value > 0);

    if (!ids.length) return;

    setLoadError("");
    try {
      await updateJobStatusByIds(ids, "Closed");
      showBulkMenuFeedback(
        `Soft archived ${ids.length} job${ids.length === 1 ? "" : "s"}.`
      );
    } catch (error) {
      console.error("Failed to soft archive jobs:", error);
      setLoadError(error?.message || "Failed to archive jobs.");
      showBulkMenuFeedback(error?.message || "Failed to archive jobs.", "error");
    }
  }, [selectedJobRows, showBulkMenuFeedback, updateJobStatusByIds]);

  const handleBulkArchiveMenuSelect = useCallback(
    (option) => {
      if (cleanText(option).toLowerCase() !== "soft_archive") return;
      handleBulkSoftArchive();
    },
    [handleBulkSoftArchive]
  );

  const handleAddAgent = useCallback(() => {
    window.alert("Add Agent isn't wired yet, but the action is now available here.");
  }, []);

  const deleteJobsByIds = useCallback(
    async (ids) => {
      if (deleteSaving) return [];

      const normalizedIds = (Array.isArray(ids) ? ids : [])
        .map((value) => Number(value))
        .filter((value) => Number.isFinite(value) && value > 0);

      if (!normalizedIds.length) return [];

      setDeleteSaving(true);
      setLoadError("");

      try {
        const res = await authFetch(`${API_BASE}/api/jobs/bulk-delete`, {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ids: normalizedIds }),
        });

        if (!res.ok) {
          const text = await res.text().catch(() => "");
          throw new Error(text || `Failed to delete jobs (${res.status})`);
        }

        const payload = await res.json().catch(() => ({}));
        const deletedIds = Array.isArray(payload?.deleted_ids)
          ? payload.deleted_ids
              .map((value) => Number(value))
              .filter((value) => Number.isFinite(value) && value > 0)
          : normalizedIds;
        notifyCompanyRelationshipsChanged();
        const deletedIdSet = new Set(deletedIds.map((value) => String(value)));

        setJobData((prev) => prev.filter((row) => !deletedIdSet.has(String(row?.id))));
        setSelectedJob((prev) =>
          prev && deletedIdSet.has(String(prev?.id)) ? null : prev
        );
        setDetailJob((prev) =>
          prev && deletedIdSet.has(String(prev?.id)) ? null : prev
        );
        setSelectedIds((prev) => prev.filter((id) => !deletedIdSet.has(String(id))));
        setSelectedIdsLocal((prev) => prev.filter((id) => !deletedIdSet.has(String(id))));

        return deletedIds;
      } catch (error) {
        console.error("Failed to bulk delete jobs:", error);
        setLoadError(error?.message || "Failed to delete jobs.");
        throw error;
      } finally {
        setDeleteSaving(false);
      }
    },
    [authFetch, deleteSaving]
  );

  const handleBulkDelete = useCallback(async (_event, options = {}) => {
    if (!isAdminUser || !selectedJobRows.length || deleteSaving) return;

    const ids = selectedJobRows
      .map((row) => Number(row?.id))
      .filter((value) => Number.isFinite(value) && value > 0);

    if (!ids.length) return;

    try {
      await deleteJobsByIds(ids);
    } catch {
      // Error state is already surfaced through setLoadError.
    }
  }, [deleteJobsByIds, deleteSaving, isAdminUser, selectedJobRows]);

  const handleEditRowJob = useCallback(
    (row) => {
      if (!row) return;
      handleCloseRowMenu();
      openDetails(row, "details");
    },
    [handleCloseRowMenu, openDetails]
  );

  const handleDuplicateRowJob = useCallback(
    (row) => {
      handleCloseRowMenu();
      window.alert(
        `Duplicate Job isn't wired yet for "${row?.job_title || "this job"}".`
      );
    },
    [handleCloseRowMenu]
  );

  const handleShareRowJob = useCallback(
    async (row) => {
      if (!row) return;

      handleCloseRowMenu();
      try {
        await createAndCopyPublicShareLink({
          apiBase: API_BASE,
          payload: buildPublicSharePayload("job", row),
        });
        setStatusToast({
          open: true,
          severity: "success",
          message: SHARE_LINK_COPIED_MESSAGE,
        });
      } catch (error) {
        console.error("Failed to share job:", error);
        setStatusToast({
          open: true,
          severity: "error",
          message: error?.message || "Failed to create share link.",
        });
      }
    },
    [handleCloseRowMenu]
  );

  const handleArchiveRowJob = useCallback(
    async (row) => {
      const jobId = Number(row?.id || 0);
      if (!Number.isFinite(jobId) || jobId <= 0) return;

      handleCloseRowMenu();
      try {
        const appliedStatus = await updateJobStatusByIds([jobId], "Closed");
        setStatusToast({
          open: true,
          severity: "success",
          message: `Archived "${row?.job_title || "job"}" as "${normalizeJobStatusDisplay(appliedStatus)}".`,
        });
      } catch (error) {
        console.error("Failed to archive job:", error);
        setStatusToast({
          open: true,
          severity: "error",
          message: error?.message || "Failed to archive job.",
        });
      }
    },
    [handleCloseRowMenu, updateJobStatusByIds]
  );

  const handleDeleteRowJob = useCallback(
    async (row) => {
      const jobId = Number(row?.id || 0);
      if (!isAdminUser || !Number.isFinite(jobId) || jobId <= 0 || deleteSaving) return;

      handleCloseRowMenu();
      const confirmed = window.confirm(
        `Delete "${row?.job_title || "this job"}"? This cannot be undone.`
      );
      if (!confirmed) return;

      try {
        await deleteJobsByIds([jobId]);
      } catch {
        // Error state is already surfaced through setLoadError.
      }
    },
    [deleteJobsByIds, deleteSaving, handleCloseRowMenu, isAdminUser]
  );

  const handlePublishJobPosting = useCallback(() => {
    if (!selectedJobRows.length) return;

    navigate("/content?tab=job_postings", {
      state: {
        from: "jobs",
        action: "publish-job-posting",
        selectedJobIds: selectedJobRows
          .map((row) => Number(row?.id))
          .filter((value) => Number.isFinite(value) && value > 0),
      },
    });

    window.setTimeout(() => {
      window.alert("Publish Job Posting opens Content -> Job Postings. The creation flow is still TBC.");
    }, 0);
  }, [navigate, selectedJobRows]);

  const handleLaunchRecruitingSequence = useCallback(() => {
    if (!selectedCount) return;
    handleOpenAddToSequence();
  }, [handleOpenAddToSequence, selectedCount]);

  const handleMatchCandidates = useCallback(async () => {
    if (matchingCandidates) return;

    const jobs = selectedJobRows.filter((row) => Number.isFinite(Number(row?.id)));
    if (!jobs.length) {
      window.alert("Select at least one job first.");
      return;
    }

    setMatchingCandidates(true);

    try {
      const res = await authFetch(`${API_BASE}/api/jobs/match-candidates`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          job_ids: jobs.map((row) => Number(row.id)),
        }),
      });

      const payload = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(payload?.error || `Failed to match candidates (${res.status})`);
      }

      const results = Array.isArray(payload?.results) ? payload.results : [];
      const successful = results.filter((item) => Number(item?.matched_count || 0) > 0);
      const totalMatches = Number(payload?.total_matches || 0);

      setReloadKey((prev) => prev + 1);

      if (!successful.length) {
        window.alert("No candidate matches were generated for the selected job(s).");
        return;
      }

      const targetJobId = Number(successful[0]?.job_id || jobs[0]?.id || 0);
      const targetJob =
        jobs.find((row) => Number(row?.id) === targetJobId) ||
        jobData.find((row) => Number(row?.id) === targetJobId) ||
        jobs[0] ||
        null;

      const nextDetailJob = targetJob
        ? {
            ...targetJob,
            matches_count: Number(successful[0]?.matched_count || 0),
            matchesCount: Number(successful[0]?.matched_count || 0),
            best_match_percentage: Number(successful[0]?.top_score || targetJob?.best_match_percentage || 0),
          }
        : null;

      const summary =
        successful.length === 1
          ? `Matched ${successful[0].matched_count} candidates for ${successful[0].job_title || targetJob?.job_title || "the selected job"}.`
          : `Matched ${totalMatches} candidates across ${successful.length} jobs.`;

      window.alert(summary);

      setQuickOpen(false);
      setSelectedJob(null);
      if (nextDetailJob) {
        setDetailJob(nextDetailJob);
        onDetailOpen?.(nextDetailJob);
      }
      updateJobSearchParam(
        targetJobId,
        false,
        "matches",
        nextDetailJob?.company_id || nextDetailJob?.companyId || targetJob?.company_id || targetJob?.companyId || ""
      );
    } catch (error) {
      console.error("Failed to match job candidates:", error);
      window.alert(error?.message || "Failed to match candidates for the selected job(s).");
    } finally {
      setMatchingCandidates(false);
    }
  }, [authFetch, jobData, matchingCandidates, onDetailOpen, selectedJobRows, updateJobSearchParam]);

  const bulkActionsNode = useMemo(
    () => (
      <BulkActionsBar
        selectedCount={selectedCount}
        selectionLabel="job"
        actions={[
          {
            type: "menu",
            label: "Change Status",
            options: jobStatusOptions,
            onSelect: handleBulkStatusChange,
            feedback: statusToast,
            onFeedbackClose: closeStatusToast,
            confirm: false,
            disabledMessage: !selectedCount ? "Please select an item to change status." : "",
            disabled: !selectedCount,
          },
          { type: "divider" },
          {
            type: "node",
            key: "assign-team-member",
            node: (
              <JobBulkAssignTeamMemberAction
                apiBase={API_BASE}
                selectedCount={selectedCount}
                onAssignTeamMember={handleAssignTeamMember}
              />
            ),
          },
          { type: "divider" },
          {
            type: "button",
            label: "Add Agent",
            startIcon: <BoltOutlinedIcon sx={{ fontSize: 18 }} />,
            onClick: handleAddAgent,
            confirm: false,
          },
          { type: "divider" },
          {
            type: "menu",
            label: "Export",
            options: JOB_EXPORT_MENU_OPTIONS,
            onSelect: handleBulkExport,
            feedback: statusToast,
            onFeedbackClose: closeStatusToast,
            confirm: false,
            disabledMessage: !selectedCount ? "Please select at least one job to export." : "",
            disabled: !selectedCount,
          },
          { type: "divider" },
          {
            type: "menu",
            label: "Archive",
            options: JOB_ARCHIVE_MENU_OPTIONS,
            onSelect: handleBulkArchiveMenuSelect,
            feedback: statusToast,
            onFeedbackClose: closeStatusToast,
            confirm: false,
            disabledMessage: !selectedCount ? "Please select at least one job to archive." : "",
            disabled: !selectedCount,
          },
          { type: "divider" },
          {
            type: "button",
            label: deleteSaving ? "Deleting..." : "Delete",
            onClick: handleBulkDelete,
            disabled: !selectedCount || deleteSaving || !isAdminUser,
            confirmTitle: "Confirm Delete",
            confirmMessage: `Delete ${selectedCount} selected job${selectedCount === 1 ? "" : "s"}? This cannot be undone.`,
            confirmLabel: "Delete",
          },
          { type: "divider" },
          {
            type: "button",
            label: "Publish Job Posting",
            onClick: handlePublishJobPosting,
            confirm: false,
            disabled: !selectedCount,
          },
          { type: "divider" },
          {
            type: "button",
            label: "Launch Recruiting Sequence",
            onClick: handleLaunchRecruitingSequence,
            confirm: false,
            disabled: !selectedCount,
          },
        ]}
        rightButton={{
          label: matchingCandidates ? "Matching..." : "Match Candidates",
          confirm: true,
          background: "var(--primary-color)",
          onClick: handleMatchCandidates,
          disabled: !selectedCount || matchingCandidates,
          sx: toolbarPillButtonSx,
        }}
      />
    ),
    [
      deleteSaving,
      handleAddAgent,
      handleAssignTeamMember,
      handleBulkArchiveMenuSelect,
      handleBulkDelete,
      handleBulkExport,
      handleBulkStatusChange,
      closeStatusToast,
      handleMatchCandidates,
      handleLaunchRecruitingSequence,
      handlePublishJobPosting,
      isAdminUser,
      jobStatusOptions,
      matchingCandidates,
      selectedCount,
      statusToast,
    ]
  );

  const contactBulkActionsNode = useMemo(
    () => (
      <BulkActionsBar
        selectedCount={selectedContactIds.length}
        selectionLabel="contact"
        actions={[]}
      />
    ),
    [selectedContactIds.length]
  );

  const topStats = useMemo(() => {
    if (showingRecruitingPipeline) {
      const pipelineStages = [
        ["New", "new"],
        ["Applied", "applied"],
        ["Matched", "matched"],
        ["Sourced", "sourced"],
        ["Contacted", "contacted"],
        ["Screened", "screened"],
        ["Presented", "presented"],
        ["Interviewing", "interview"],
        ["Assessment", "assessment"],
        ["Offer", "offer"],
        ["Hired", "hired"],
        ["Rejected", "rejected"],
      ];

      return pipelineStages.map(([label, stage]) => ({
        id: `pipeline:${stage}`,
        label,
        value: String(
          recruitingStageStats[pipelineStageKey(stage)] || 0
        ),
        ...recruitingMetricColors(stage),
      }));
    }

    if (showingContacts) {
      const total = enrichedContactRows.length;
      const hiringManagerCount = enrichedContactRows.filter((row) =>
        isHiringManagerContactRow(row)
      ).length;
      const jobContacts = enrichedContactRows.filter(
        (row) => normalizeHiringManagerEntityType(row?.entity_type) === "job"
      ).length;
      const companyContacts = enrichedContactRows.filter((row) => {
        const entityType = normalizeHiringManagerEntityType(row?.entity_type);
        return entityType === "company" || entityType === "client";
      }).length;
      const withSource = enrichedContactRows.filter((row) => {
        const source = String(row?.source || "").trim();
        return Boolean(source) && source !== "-";
      }).length;

      return [
        {
          id: "total",
          value: String(Number.isFinite(Number(contactRowsTotal)) ? contactRowsTotal : total),
          label: "Total Contacts",
        },
        { id: "hiringManager", value: String(hiringManagerCount), label: "Hiring Managers" },
        { id: "jobContacts", value: String(jobContacts), label: "Job Contacts" },
        { id: "companyContacts", value: String(companyContacts), label: "Company Contacts" },
        { id: "withSource", value: String(withSource), label: "With Source" },
      ];
    }

    return [
      { id: "status:open", value: String(pipelineStats.open_jobs ?? 0), label: "Open Jobs" },
      { id: "assignedRecruiters", value: String(pipelineStats.assigned_recruiters ?? 0), label: "Assigned Recruiters" },
      { id: "applied", value: String(pipelineStats.applied ?? 0), label: "Applied" },
      { id: "matched", value: String(pipelineStats.matched ?? 0), label: "Matched (by AI)" },
      { id: "sourced", value: String(pipelineStats.sourced ?? 0), label: "Sourced" },
      { id: "contacted", value: String(pipelineStats.contacted ?? 0), label: "Contacted" },
      { id: "screened", value: String(pipelineStats.screened ?? 0), label: "Screened" },
      { id: "presented", value: String(pipelineStats.presented ?? 0), label: "Presented" },
      { id: "interviewing", value: String(pipelineStats.interviewing ?? 0), label: "Interviewing" },
      { id: "assessments", value: String(pipelineStats.assessments ?? 0), label: "Assessments" },
      { id: "offers", value: String(pipelineStats.offers ?? 0), label: "Offers" },
      { id: "hires", value: String(pipelineStats.hires ?? 0), label: "Hires" },
    ].map((item) => ({
      ...item,
      description: JOB_STATS_ITEM_DESCRIPTIONS[item.id] || "",
    }));
  }, [contactRowsTotal, enrichedContactRows, pipelineStats, recruitingStageStats, showingContacts, showingRecruitingPipeline]);

  const handleStatItemClick = useCallback((item) => {
    const nextId = String(item?.id || "");
    if (showingRecruitingPipeline && nextId.startsWith("pipeline:")) {
      const selectedStage = nextId.slice("pipeline:".length);
      const isAlreadySelected =
        normalizeRecruitingPipelineStage(recruitingPipelineStage) === selectedStage;
      const nextStage = isAlreadySelected ? "" : selectedStage;
      const params = new URLSearchParams(location.search);
      params.set("view", "recruiting-pipeline");
      if (nextStage) params.set("stage", nextStage);
      else params.delete("stage");
      setRecruitingPipelineStage(nextStage);
      setRecruitingPipelinePage(1);
      navigate(
        {
          pathname: location.pathname,
          search: `?${params.toString()}`,
        },
        { replace: true }
      );
      return;
    }
    if (!nextId || nextId === "total") {
      setActiveStatId("total");
      return;
    }

    const isStatusMetric = nextId.startsWith("status:");
    const isAssignedRecruitersMetric = nextId === "assignedRecruiters";
    const isPipelineStageMetric = Boolean(
      JOB_PIPELINE_STAGE_FILTER_VALUES_BY_STAT_ID[nextId]
    );
    const isAlreadyActive = activeStatId === nextId;

    // Status metrics must be applied to the API request instead of filtering
    // just the current page of jobs in memory. That keeps the result total in
    // sync with the count shown on the stats card.
    if (isStatusMetric) {
      const status = nextId.slice("status:".length);
      setAppliedFilters((previous) => ({
        ...previous,
        statuses: isAlreadyActive ? [] : [status],
      }));
      setDraftFilters((previous) => ({
        ...previous,
        statuses: isAlreadyActive ? [] : [status],
      }));
    }

    if (isAssignedRecruitersMetric) {
      // The API exposes this as the advanced "has recruiter" filter. It is
      // required here so the card filters every job, not merely the jobs on
      // the currently loaded table page.
      setAppliedFilters((previous) => ({
        ...previous,
        statuses: isAlreadyActive ? [] : ["Open"],
      }));
      setDraftFilters((previous) => ({
        ...previous,
        statuses: isAlreadyActive ? [] : ["Open"],
      }));
      setAdvancedApplied((previous) => {
        const next = normalizeJobAdvancedFilters(previous);
        return {
          ...next,
          team: { ...next.team, recruiter: !isAlreadyActive },
        };
      });
    } else if (isPipelineStageMetric) {
      // Pipeline cards are global stage views. Clear the prior Open-status
      // metric so the selected stage's card total and table total use the
      // same query.
      setAppliedFilters((previous) => ({ ...previous, statuses: [] }));
      setDraftFilters((previous) => ({ ...previous, statuses: [] }));
    } else if (activeStatId === "assignedRecruiters") {
      setAdvancedApplied((previous) => {
        const next = normalizeJobAdvancedFilters(previous);
        return {
          ...next,
          team: { ...next.team, recruiter: false },
        };
      });
    }

    setActiveStatId(isAlreadyActive ? "total" : nextId);
    setPage(1);
  }, [
    activeStatId,
    location.pathname,
    location.search,
    navigate,
    recruitingPipelineStage,
    showingRecruitingPipeline,
  ]);
  const [activeDetailTab, setActiveDetailTab] = useState(
    detailRouteTab || "details"
  );
  const detailOriginContext = useMemo(() => {
    const origin = location.state?.companyOrigin;
    if (!origin?.parentCompany?.id) return null;

    return {
      parentCompany: origin.parentCompany,
      parentTab: normalizeCompanyOriginTab(origin.parentTab),
      returnListTab: origin.returnListTab === "clients" ? "clients" : "companies",
    };
  }, [location.state]);
  const detailOriginCompanyLabel = useMemo(() => {
    if (!detailOriginContext?.parentCompany) return "";
    return (
      detailOriginContext.parentCompany?.name ||
      detailOriginContext.parentCompany?.company_name ||
      "Company"
    );
  }, [detailOriginContext]);
  const detailOriginParentTabLabel = useMemo(() => {
    if (!detailOriginContext) return "";
    return findTabLabel(COMPANY_PROFILE_TABS, detailOriginContext.parentTab);
  }, [detailOriginContext]);
  const activeDetailBreadcrumbLabel = useMemo(
    () => findTabLabel(DETAIL_VIEW_TABS, activeDetailTab),
    [activeDetailTab]
  );
  const detailJobCompanyLabel = useMemo(
    () => cleanText(detailJob?.company_name || detailJob?.company || detailJob?.companyName || ""),
    [detailJob]
  );
  const openCompaniesPage = useCallback(
    (search = "", payload = null) => {
      const nextSearch = String(search || "");

      try {
        if (payload) {
          sessionStorage.setItem(NAV_CACHE_KEY, JSON.stringify(payload));
        } else {
          sessionStorage.removeItem(NAV_CACHE_KEY);
        }
      } catch {
        // ignore session storage failures
      }

      if (typeof window !== "undefined" && window.location) {
        const currentPath = String(window.location.pathname || "");
        const currentSearch = String(window.location.search || "");
        const currentHash = String(window.location.hash || "");
        const nextPath = "/companies";
        const nextUrl = `${nextPath}${nextSearch}`;

        if (
          currentPath !== nextPath ||
          currentSearch !== nextSearch ||
          currentHash
        ) {
          window.location.assign(nextUrl);
          return;
        }
      }

      navigate(
        {
          pathname: "/companies",
          search: nextSearch,
        },
        {
          replace: false,
          state: payload || createSidebarResetState("/companies"),
        }
      );
    },
    [navigate]
  );
  const openCompaniesList = useCallback(() => {
    openCompaniesPage("");
  }, [openCompaniesPage]);
  const openDetailJobCompany = useCallback(() => {
    const companyId = Number(detailJob?.company_id || detailJob?.companyId || 0);
    if (!Number.isFinite(companyId) || companyId <= 0) return;

    const companyName =
      cleanText(detailJob?.company_name || detailJob?.company || detailJob?.companyName || "") ||
      "Company";
    const payload = buildOpenCompanyNavPayload({
      company: {
        id: companyId,
        company_id: companyId,
        companyId: companyId,
        name: companyName,
        company_name: companyName,
        logo: cleanText(detailJob?.company_logo || detailJob?.logo || ""),
        company_logo: cleanText(detailJob?.company_logo || detailJob?.logo || ""),
        website: cleanText(
          detailJob?.company_website || detailJob?.website || detailJob?.website_url || ""
        ),
        website_url: cleanText(
          detailJob?.website_url || detailJob?.company_website || detailJob?.website || ""
        ),
        company_website: cleanText(
          detailJob?.company_website || detailJob?.website || detailJob?.website_url || ""
        ),
      },
      defaultTab: "details",
      returnListTab: "companies",
      entityType: "company",
    });
    if (!payload) return;

    openCompaniesPage(
      `?companyId=${encodeURIComponent(String(companyId))}&tab=jobs&entityType=company&returnTab=companies`,
      payload
    );
  }, [detailJob, openCompaniesPage]);
  const openOriginCompanyTab = useCallback(() => {
    if (!detailOriginContext?.parentCompany?.id) {
      closeDetails();
      return;
    }

    const { parentCompany, parentTab, returnListTab } = detailOriginContext;
    const parentEntityType =
      String(parentCompany?.entityType || "").toLowerCase() === "company" ? "company" : "lead";
    const payload = buildOpenCompanyNavPayload({
      company: parentCompany,
      defaultTab: parentTab,
      returnListTab,
      entityType: parentEntityType,
    });
    if (!payload) {
      closeDetails();
      return;
    }

    openCompaniesPage(
      `?companyId=${encodeURIComponent(parentCompany.id)}&tab=${encodeURIComponent(
        parentTab
      )}&entityType=${encodeURIComponent(parentEntityType)}&returnTab=${encodeURIComponent(
        returnListTab
      )}`,
      payload
    );
  }, [closeDetails, detailOriginContext, openCompaniesPage]);
  const openOriginCompanyProfile = useCallback(() => {
    if (!detailOriginContext?.parentCompany?.id) {
      closeDetails();
      return;
    }

    const { parentCompany, returnListTab } = detailOriginContext;
    const payload = buildOpenCompanyNavPayload({
      company: parentCompany,
      defaultTab: "jobs",
      returnListTab,
      entityType: "company",
    });
    if (!payload) {
      closeDetails();
      return;
    }

    openCompaniesPage(
      `?companyId=${encodeURIComponent(parentCompany.id)}&tab=jobs&entityType=company&returnTab=${encodeURIComponent(returnListTab)}`,
      payload
    );
  }, [closeDetails, detailOriginContext, openCompaniesPage]);

  useEffect(() => {
    setActiveDetailTab(detailRouteTab || "details");
  }, [detailRouteTab]);

  if (showAdvancedSearch) {
    return (
      <Box sx={{ py: 0, pb: 3, border: 0 }}>
        <Breadcrumb
          items={[
            { label: "Overview", onClick: () => navigate("/overview") },
            { label: "Jobs", path: "/jobs" },
            { label: "Advance Search" },
          ]}
        />

        <Box>
          <CompanyAdvancedSearchView
            title="Jobs - Advance Search"
            initialFilters={mergeBasicIntoAdvancedJobFilters(appliedFilters, advancedApplied)}
            primarySectionTitle="Job Details"
            options={advancedSearchOptions}
            jobSearchMode
            onClose={closeAdvancedSearch}
            onApply={applyAdvancedSearch}
          />
        </Box>
      </Box>
    );
  }

  if (isDetailRouteActive && (!detailJob || String(detailJob.id) !== String(detailRouteJobId))) {
    return <Box sx={{ p: 3 }}><Stack direction="row" spacing={1.5} alignItems="center">
      <CircularProgress size={18} /><Typography>Loading job profile...</Typography>
    </Stack></Box>;
  }

  if (detailJob && isDetailRouteActive) {
    return (
      <Box sx={{ py: 0, pb: 3, border: 0 }}>
        <Breadcrumb
          activeLabel={activeDetailBreadcrumbLabel}
          items={
            detailOriginContext
              ? [
                  { label: "Overview", onClick: () => navigate("/overview") },
                  { label: "Companies", onClick: openCompaniesList },
                  ...(detailOriginCompanyLabel
                    ? [{ label: detailOriginCompanyLabel, onClick: openOriginCompanyProfile }]
                    : []),
                  ...(detailOriginParentTabLabel
                    ? [{ label: detailOriginParentTabLabel, onClick: openOriginCompanyTab }]
                    : []),
                  { label: detailJob?.job_title || "Job Details" },
                ]
              : [
                  { label: "Overview", onClick: () => navigate("/overview") },
                  { label: "Jobs", path: "/jobs" },
                  ...(detailJobCompanyLabel
                    ? [{ label: detailJobCompanyLabel, onClick: openDetailJobCompany }]
                    : []),
                  { label: detailJob?.job_title || "Job Details" },
                ]
          }
        />

        <Box>
          <JobDetailsView
            job={detailJob}
            detailFieldOptions={jobDetailFieldOptions}
            initialTab={detailRouteTab || "details"}
            onBack={closeDetails}
            hideBreadcrumb={true}
            onTabChange={setActiveDetailTab}
            onJobChange={(nextJob) => {
              if (!nextJob?.id) return;
              setDetailJob((previous) =>
                String(previous?.id) === String(nextJob.id)
                  ? { ...(previous || {}), ...nextJob }
                  : previous
              );
              setJobData((previous) =>
                previous.map((row) =>
                  String(row?.id) === String(nextJob.id) ? { ...row, ...nextJob } : row
                )
              );
            }}
          />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ py: 0, pb: 3, border: 0 }} >
      {!!loadError && (
        <Box sx={{ pb: 2 }}>
          <Alert severity="error">{loadError}</Alert>
        </Box>
      )}

      <ListTableTabsTemplate
        breadcrumb={
          <Breadcrumb
            activeLabel={showingContacts ? "Contacts" : "Jobs"}
            items={[
              { label: "Overview", onClick: () => navigate("/overview") },
              { label: "Jobs", path: "/jobs" },
            ]}
          />
        }
        statsSection={
          <StatsSlider
            items={topStats}
            autoPlay={false}
            showArrows={true}
            cardMinWidth={200}
            gap={12}
            onItemClick={handleStatItemClick}
            activeItemId={
              showingRecruitingPipeline && recruitingPipelineStage
                ? `pipeline:${normalizeRecruitingPipelineStage(recruitingPipelineStage)}`
                : activeStatId
            }
          />
        }
        contentSection={
          <>
            <Card
              sx={{
                p: 0,
                backgroundColor: "#fff",
                boxShadow: "none",

                overflow: "hidden",
              }}
            >
              <TopListHeaderBar
                title={showingContacts ? "Contacts" : showingRecruitingPipeline ? "Recruiting Pipeline" : "Jobs"}
                searchPlaceholder={showingContacts ? "Search contacts..." : "Search candidates..."}
                searchValue={search}
                onSearchChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                  if (showingContacts) setContactTablePage(1);
                  if (showingRecruitingPipeline) setRecruitingPipelinePage(1);
                }}
                actions={showingContacts || showingRecruitingPipeline ? [
                  { label: "Schedule", icon: <EventOutlinedIcon />, onClick: () => openInterviewScheduler(), variant: "contained" },
                  { label: "Filter By", icon: <Filter size="18" color="currentColor" variant="Linear" />, onClick: openFilter, variant: "contained" },
                ] : [
                  { label: "Filter By", icon: <Filter size="18" color="currentColor" variant="Linear" />, onClick: openFilter, variant: "contained" },
                  { label: "Import CSV", icon: <Briefcase size="18" color="currentColor" variant="Linear" />, onClick: () => setCsvImportOpen(true), variant: "contained" },
                  { label: "Add Agent", icon: <BoltOutlinedIcon sx={{ fontSize: 18 }} />, onClick: handleAddAgent, variant: "contained" },
                  { label: "Add New Job", icon: <Briefcase size="18" color="currentColor" variant="Linear" />, onClick: () => setAddJobOpen(true), variant: "contained" },
                  { label: "Schedule", icon: <EventOutlinedIcon />, onClick: () => openInterviewScheduler(), variant: "contained" },
                ]}
                resultsToolbarTargetId="jobs-results-toolbar"
              />

              <Box sx={{ px: 2, borderBottom: "1px solid #E5E7EB" }}>
                <TabNavigationBar
                  value={listTab}
                  onChange={(nextValue) => {
                    setListTab(nextValue);
                    if (nextValue !== "recruiting-pipeline") {
                      setRecruitingPipelineStage("");
                    }
                  }}
                  items={[
                    {
                      value: "jobs",
                      label: "Jobs",
                      count: allJobsTotal,
                    },
                    {
                      value: "recruiting-pipeline",
                      label: "Recruiting Pipeline",
                      count: recruitingPipelineTotal ?? recruitingPipelineRows.length,
                    },
                    {
                      value: "contacts",
                      label: "Contacts",
                      count: contactRowsTotal ?? enrichedContactRows.length,
                    },
                  ]}
                  borderBottom={false}
                />
              </Box>

              {showingRecruitingPipeline ? (
                <>
                    <RecruitingPipelineTable
                      scopeKey={JSON.stringify([search, recruitingPipelineStage])}
                      request={authFetch}
                      apiBase={API_BASE}
                      user={user}
                      loading={recruitingPipelineLoading}
                      onMoved={() => setReloadKey(value => value + 1)}
                      columns={recruitingPipelineColumns}
                      rows={recruitingPipelineRows}
                      renderRow={renderRecruitingPipelineRow}
                      defaultOrderBy="job_stage"
                      defaultOrderDirection="asc"
                      resetPageSignal={tableResetSignal}
                      includeSelectionColumn
                      visualVariant="company-standard"
                      fitToContainer
                      resultsToolbarTargetId="jobs-results-toolbar"
                      initialRowsPerPage={10}
                      serverSidePagination
                      resultsTotalCount={recruitingPipelineFilteredTotal}
                      serverPage={recruitingPipelinePage - 1}
                      serverRowsPerPage={recruitingPipelineRowsPerPage}
                      onServerPageChange={handleRecruitingPipelinePageChange}
                      onServerRowsPerPageChange={handleRecruitingPipelineRowsPerPageChange}
                      tableSx={{
                        // Match the Candidate column's left edge to the
                        // Recruiting Pipeline heading above the table.
                        "& .MuiTableHead-root .MuiTableCell-root:first-of-type, & .MuiTableBody-root .MuiTableCell-root:first-of-type": {
                          pl: 2,
                        },
                      }}
                    />
                </>
              ) : showingContacts ? (
                <GenericDataTable
                  columns={contactColumns}
                  exportFields={[...columns, ...contactColumns, ...recruitingPipelineColumns, ...JOB_EXPORT_TAB_FIELDS].filter((field) => field.id !== "actions")}
                  rows={filteredContactRows}
                  renderRow={renderContactRow}
                  defaultOrderBy="updated_at"
                  defaultOrderDirection="desc"
                  resetPageSignal={tableResetSignal}
                  includeSelectionColumn
                  visualVariant="company-standard"
                  fitToContainer
                  resultsToolbarTargetId="jobs-results-toolbar"
                  initialRowsPerPage={10}
                  serverSidePagination={activeStatId === "total"}
                  resultsTotalCount={contactTableTotal}
                  resultsFilterPills={contactFilterPills(contactFilters, next => { setContactFilters(next); setContactTablePage(1); })}
                  serverPage={contactTablePage - 1}
                  serverRowsPerPage={contactTableRowsPerPage}
                  onServerPageChange={handleContactServerPageChange}
                  onServerRowsPerPageChange={handleContactServerRowsPerPageChange}
                  selectedIds={selectedContactIds}
                  onSelectedIdsChange={(ids) =>
                    setSelectedContactIds(
                      Array.isArray(ids) ? ids.map((id) => String(id)) : []
                    )
                  }
                  bulkActionsTop={contactBulkActionsNode}
                  bulkActionsBottom={contactBulkActionsNode}
                />
              ) : jobsLoading && !jobData.length ? (
                <Box sx={{ p: 3 }}>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <CircularProgress size={18} />
                    <Typography sx={{ fontWeight: 700 }}>Loading jobs...</Typography>
                  </Stack>
                </Box>
              ) : (
                <Box
                  onChangeCapture={(e) => {
                    const el = e.target;
                    if (!el || el.tagName?.toLowerCase() !== "input") return;
                    if (el.type !== "checkbox") return;

                    const inThead = el.closest("thead");
                    if (inThead) {
                      if (el.checked) setSelectedIdsLocal(filteredJobRows.map((r) => r.id));
                      else setSelectedIdsLocal([]);
                      return;
                    }

                    const tr = el.closest("tr");
                    const rawId = tr?.getAttribute("data-id") || tr?.dataset?.id;
                    const id = normalizeId(rawId);
                    if (id === null) return;

                    setSelectedIdsLocal((prev) => {
                      const set = new Set((prev || []).map((x) => String(x)));
                      if (el.checked) set.add(String(id));
                      else set.delete(String(id));
                      return Array.from(set).map((x) => normalizeId(x));
                    });
                  }}
                >
                  <GenericDataTable
                    columns={columns}
                    exportFields={[...columns, ...contactColumns, ...recruitingPipelineColumns, ...JOB_EXPORT_TAB_FIELDS].filter((field) => field.id !== "actions")}
                    rows={filteredJobRows}
                    renderRow={renderRow}
                    defaultOrderBy="job_list_last_activity_time"
                    defaultOrderDirection="desc"
                    resetPageSignal={tableResetSignal}
                    includeSelectionColumn={true}
                    visualVariant="company-standard"
                    fitToContainer
                    resultsToolbarTargetId="jobs-results-toolbar"
                    selectionColumnWidth={JOBS_SELECTION_COLUMN_WIDTH_PX}
                    selectionCellSx={{
                      width: `${JOBS_SELECTION_COLUMN_WIDTH_PX}px !important`,
                      minWidth: `${JOBS_SELECTION_COLUMN_WIDTH_PX}px !important`,
                      maxWidth: `${JOBS_SELECTION_COLUMN_WIDTH_PX}px !important`,
                      pl: `${JOBS_BULK_ACTIONS_LABEL_INSET_PX}px !important`,
                      pr: "0 !important",
                      pt: `${JOBS_SELECTION_CELL_PADDING_Y_PX}px !important`,
                      pb: `${JOBS_SELECTION_CELL_PADDING_Y_PX}px !important`,
                      verticalAlign: "top",
                      boxSizing: "border-box",
                    }}
                    selectionCellStyle={{
                      width: `${JOBS_SELECTION_COLUMN_WIDTH_PX}px`,
                      minWidth: `${JOBS_SELECTION_COLUMN_WIDTH_PX}px`,
                      maxWidth: `${JOBS_SELECTION_COLUMN_WIDTH_PX}px`,
                      paddingLeft: `${JOBS_BULK_ACTIONS_LABEL_INSET_PX}px`,
                      paddingRight: "0px",
                      paddingTop: `${JOBS_SELECTION_CELL_PADDING_Y_PX}px`,
                      paddingBottom: `${JOBS_SELECTION_CELL_PADDING_Y_PX}px`,
                      verticalAlign: "top",
                      boxSizing: "border-box",
                    }}
                    tableContainerSx={{
                      overflowX: "hidden !important",
                    }}
                    tableSx={{
                      tableLayout: "fixed",
                      width: "100%",
                      "& .MuiTableCell-root": {
                        overflow: "hidden",
                      },
                      "& .MuiTableHead-root .MuiTableCell-root": {
                        px: 0.75,
                        py: 0.85,
                        fontSize: 14,
                        color: "#667085",
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                      },
                      "& .MuiTableBody-root .MuiTableCell-root": {
                        px: 0.75,
                        py: 0.9,
                        verticalAlign: "middle",
                      },
                      "& .MuiTableCell-paddingCheckbox": {
                        width: `${JOBS_SELECTION_COLUMN_WIDTH_PX}px !important`,
                        minWidth: `${JOBS_SELECTION_COLUMN_WIDTH_PX}px !important`,
                        maxWidth: `${JOBS_SELECTION_COLUMN_WIDTH_PX}px !important`,
                        pl: `${JOBS_BULK_ACTIONS_LABEL_INSET_PX}px !important`,
                        pr: "0 !important",
                        pt: `${JOBS_SELECTION_CELL_PADDING_Y_PX}px !important`,
                        pb: `${JOBS_SELECTION_CELL_PADDING_Y_PX}px !important`,
                        verticalAlign: "top",
                        boxSizing: "border-box",
                      },
                      "& .MuiTableHead-root .MuiCheckbox-root, & .MuiTableBody-root .MuiCheckbox-root": {
                        p: 0,
                        width: 18,
                        height: 18,
                        minWidth: 18,
                        minHeight: 18,
                        ml: 0,
                        mr: 0,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                      },
                    }}
                    resultsFilterPills={resultsFilterPills}
                    resultsTotalCount={
                      activeStatId === "total" ||
                      activeStatId.startsWith("status:") ||
                      activeStatId === "assignedRecruiters" ||
                      Boolean(JOB_PIPELINE_STAGE_FILTER_VALUES_BY_STAT_ID[activeStatId])
                        ? jobsTotal
                        : filteredJobRows.length
                    }
                    serverSidePagination={
                      activeStatId === "total" ||
                      activeStatId.startsWith("status:") ||
                      activeStatId === "assignedRecruiters" ||
                      Boolean(JOB_PIPELINE_STAGE_FILTER_VALUES_BY_STAT_ID[activeStatId])
                    }
                    serverPage={page - 1}
                    serverRowsPerPage={rowsPerPage}
                    onServerPageChange={handleJobPageChange}
                    onServerRowsPerPageChange={handleJobRowsPerPageChange}
                    selectedIds={effectiveSelectedIds}
                    onSelectedIdsChange={(ids) => {
                      const nextIds = Array.isArray(ids) ? ids : [];
                      setSelectedIds(nextIds);
                      setSelectedIdsLocal(nextIds);
                    }}
                    bulkActionsTop={bulkActionsNode}
                    bulkActionsBottom={bulkActionsNode}
                  />
                </Box>
              )}
            </Card>

            <Menu
              open={Boolean(rowMenuAnchorEl)}
              anchorEl={rowMenuAnchorEl}
              onClose={handleCloseRowMenu}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              PaperProps={{
                sx: {
                  mt: 0.5,
                  borderRadius: 2.5,
                  minWidth: 220,
                  boxShadow: "0 16px 40px rgba(15,23,42,0.16)",
                },
              }}
            >
              <MenuItem
                onClick={() => {
                  if (rowMenuItem) handleEditRowJob(rowMenuItem);
                }}
                sx={{ gap: 1.25, fontSize: 13, fontFamily: "Be Vietnam Pro" }}
              >
                <Magicpen size="18" color="#8B5CF6" variant="Linear" />
                Edit Job
              </MenuItem>

              <MenuItem
                onClick={() => {
                  if (rowMenuItem) handleShareRowJob(rowMenuItem);
                }}
                sx={{ gap: 1.25, fontSize: 13, fontFamily: "Be Vietnam Pro" }}
              >
                <ContentCopyOutlinedIcon sx={{ fontSize: 18, color: "#8B5CF6" }} />
                Share Link
              </MenuItem>

              <MenuItem
                onClick={() => {
                  if (rowMenuItem) handleDuplicateRowJob(rowMenuItem);
                }}
                sx={{ gap: 1.25, fontSize: 13, fontFamily: "Be Vietnam Pro" }}
              >
                <Add size="18" color="#8B5CF6" variant="Linear" />
                Duplicate Job
              </MenuItem>

              <MenuItem
                onClick={() => {
                  if (rowMenuItem) handleArchiveRowJob(rowMenuItem);
                }}
                sx={{ gap: 1.25, fontSize: 13, fontFamily: "Be Vietnam Pro" }}
              >
                <CloseCircle size="18" color="#8B5CF6" variant="Linear" />
                Archive Job
              </MenuItem>

              {isAdminUser ? <Divider sx={{ my: 0.5 }} /> : null}

              {isAdminUser ? (
                <MenuItem
                  onClick={() => {
                    if (rowMenuItem) handleDeleteRowJob(rowMenuItem);
                  }}
                  sx={{ gap: 1.25, fontSize: 13, color: "#F04438", fontFamily: "Be Vietnam Pro" }}
                >
                  <Trash size="18" color="#F04438" variant="Linear" />
                  Delete Job
                </MenuItem>
              ) : null}
            </Menu>

            <QuickJobDetailDrawer
              open={quickOpen}
              onClose={handleCloseQuickOpen}
              job={selectedJob}
              onShowMore={(job) => {
                openDetails(job);
              }}
            />

            <FilterPopup
              open={isFilterOpen && !showingContacts}
              onClose={() => setIsFilterOpen(false)}
              draft={draftFilters}
              setDraft={setDraftFilters}
              endpoint={showingRecruitingPipeline ? `${filterEndpoint}${filterEndpoint.includes('?') ? '&' : '?'}has_job=1` : filterEndpoint}
              onClear={clearFilters}
              onApply={applyFilters}
              onAdvancedSearch={openAdvancedSearch}
            />

            <ContactFiltersDrawer open={isFilterOpen && showingContacts} value={contactFilters}
              onClose={() => setIsFilterOpen(false)}
              onApply={next => { setContactFilters(next); setContactTablePage(1); }} />

            <AddJobDialog
              open={addJobOpen}
              onClose={() => setAddJobOpen(false)}
              onCreated={() => setReloadKey((prev) => prev + 1)}
              user={user}
            />
            <CsvImportDialog open={csvImportOpen} onClose={() => setCsvImportOpen(false)} entityLabel="jobs" importEndpoint={`${API_BASE}/api/csv-import/jobs`} onImported={() => setReloadKey((value) => value + 1)} />

            <AddToSequenceDialog
              open={isAddToSequenceOpen}
              onClose={() => setIsAddToSequenceOpen(false)}
              entityType="job"
              items={selectedSequenceItems}
              user={user}
              onCreated={() => {
                setSelectedIds([]);
                setSelectedIdsLocal([]);
              }}
            />
          </>
        }
      />

    </Box>
  );
};

const Jobs = () => {
  const { user, loading } = useAuthController();

  if (loading) return <p>Loading...</p>;
  if (user?.role === "candidate") return null;

  return <JobsContent user={user} />;
};

export default Jobs;
